import { Injector } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { of } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { distinctUntilChanged, filter, pairwise, scan, switchMap, tap } from 'rxjs/operators';
import { AuthService } from '../../../shared/service/auth-service/auth-service.service';
import { CodeName } from '../../../shared/service/lookup-service/code-name.model';
import { LookupGetService } from '../../../shared/service/lookup-service/lookup-get.service';
import { Art57Section } from '../../../shared/ui/Art57Section/Art57Section';
import { ListColumn, ListDataColumn } from '../../../shared/ui/Data/ListColumn';
import { AbstractControl } from '@angular/forms';
import { map } from 'rxjs';
import { SectionName } from '../../../shared/ui/SectionName';
import { DateUtil } from '../../../shared/ui/DateUtil';
import { LoggerService } from '../../../shared/service/logger-service/logger.service';
import { AutoFocus } from 'primeng/autofocus';
import { MessageService } from 'primeng/api';
import { environment } from '../../../../environments/environment';

export class FieldDefinition {
  constructor(private injector: Injector) { }

  private isPtOrLltMeddraLevel(levelName?: string): boolean {
    return (
      levelName === 'PT' ||
      levelName === 'LLT'
    );
  }

  // is valid authorised procedure / legal Basis combination
  private isValidAuthrisedProcedureLegalBasis(authorisedProcedure: number, legalBasis: number) {
    // Non EUProcedure = 5
    const validLegalBassisNonEUProcedure = [1, 2, 3, 4, 5, 6, 7, 10, 11];
    // EU other approval = 13
    const valudLegalBasisEUApproval = [8, 9, 12, 13, 14, 15, 16];
    // Rest Legal Basis Valid
    const validLegalBasisByDefault = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

    return ((authorisedProcedure == 5 && validLegalBassisNonEUProcedure.includes(legalBasis)) ||
      (authorisedProcedure == 13 && valudLegalBasisEUApproval.includes(legalBasis)) ||
      ((!([5, 13].includes(authorisedProcedure))) && validLegalBasisByDefault.includes(legalBasis))
    )
  }

  // Load options from cache
  private loadCachedOptions(lookup: string): Observable<CodeName[]> {
    return this.injector
      .get(LookupGetService)
      .retrieveFromCache(lookup)
      .pipe(
        switchMap((cachedOptions: CodeName[]) => {
          if (cachedOptions.length > 0) {
            return of(cachedOptions);
          } else {
            return this.injector
              .get(LookupGetService)
              .getAll(lookup)
              .pipe(
                tap((options: CodeName[]) =>
                  this.injector
                    .get(LookupGetService)
                    .addToCache(lookup, options)
                )
              );
          }
        })
      );
  }

  expressedAsHook(field: FormlyFieldConfig) {

    //Read strength control (Expressed as field)
    const strengthControl =
      field.parent!.parent!.parent!.parent!.fieldGroup![3].get!(
        'expressedAs'
      ).formControl;

    //Read high amount of demon unit
    let highAmountdenomunit =
      field.parent!.parent!.parent!.parent!.fieldGroup![5].fieldGroup![2]
        .fieldGroup![2].fieldGroup![0];
    const parentField = field.parent!.parent!.parent!.parent!.fieldGroup![3];
    let strengthField = field.parent!.parent!.parent!.parent!.fieldGroup![3].get!(
      'expressedAs'
    );


    let previousValueOfExpressedAS: any = undefined;


    if (strengthField && !(strengthField as any)._subscriptionInit) {
      (strengthField as any)._subscriptionInit = true;
      const subscription = strengthControl!.valueChanges.subscribe((value) => {
        // Low amount denom unit has changed or value is undefined
        if (previousValueOfExpressedAS != value || value == undefined) {
          // check value of expressed as (strenght) field (value) to load valid options
          let optionsToDisplay;
          let lookUpValueSet = '';
          let valueToSet = value;
          let valueToSearch = undefined

          if (value == undefined) {
            //Expressed as if empty then we set the low amount denom unit
            valueToSearch = field!.formControl!.value;
          } else if (previousValueOfExpressedAS != undefined) {
            //Expressed as has changed again
            valueToSearch = undefined;
          } else {
            //Expressed as previous value is undefined (typically the first time)
            valueToSearch = field!.formControl!.value;
          }

          if (valueToSet == 1) {
            lookUpValueSet = 'unitofmeasurement';
          } else {
            valueToSet = 2;
            lookUpValueSet = 'unitofpresentation';
          }

          if (valueToSearch && valueToSearch != undefined && this.injector
            .get(LookupGetService)
            .retrieveValueFromCache(lookUpValueSet, valueToSearch) == '') {
            lookUpValueSet = 'unitofmeasurement'; // by default we load unitofmeasurement options (except if we are loading a unit of presentation)
            valueToSet = 1;
          } else if (valueToSearch == undefined && value == previousValueOfExpressedAS) {
            lookUpValueSet = 'unitofmeasurement'; // by default we load unitofmeasurement options (except if we are loading a unit of presentation)
            valueToSet = 1;
          }

          if (valueToSearch == 0) {
            optionsToDisplay = [{ 'code': 0, 'name': '***' }]
          } else {
            optionsToDisplay = this.injector
              .get(LookupGetService)
              .loadCachedOptions(lookUpValueSet);
          }

          // load valid options to low amount denom unit and high amount denom unit select fields
          if (field.props) {
            field.props.options = optionsToDisplay;
          }

          if (highAmountdenomunit?.props) {
            highAmountdenomunit.props.options = optionsToDisplay;
          }

          let valueIsSet =
            field.formControl != undefined && field.formControl?.value != null;

          let valueExistsInLookup: boolean = false;
          if (valueIsSet) {
            // check if value of low amount denom unit (current form control) exists in loaded options
            valueExistsInLookup = field.formControl?.value == 0 ? true :
              this.injector
                .get(LookupGetService)
                .retrieveValueFromCache(
                  lookUpValueSet,
                  field.formControl!.value
                ) != '';
          }

          // clear value from low amount denom unit if not exist in loaded options
          if (!valueExistsInLookup) {
            field.formControl?.setValue(null);
            //  (sync) set high amount denom unit value with same value than low amount denom unit
          }
          highAmountdenomunit.formControl?.setValue(field.formControl?.value);
          previousValueOfExpressedAS = valueToSet;
          if (value != previousValueOfExpressedAS) {
            value = valueToSet;
            strengthControl?.setValue(valueToSet);
          }
        }
      });
      (strengthField as any)._subscription = subscription;

      strengthControl?.setValue(strengthControl.value);

    }
  }

  getMeddraVersions(): Observable<CodeName[]> {
    if (
      this.injector.get(Art57Section).selectionSectionName ==
      SectionName.createandsendproductreports
    ) {
      return this.injector
        .get(LookupGetService)
        .loadCachedOptions('supportedmeddraversions');
    } else {
      return this.injector
        .get(LookupGetService)
        .loadCachedOptions('supportedbacklogmeddraversions');
    }
  }

  getFieldOperationType(field: FormlyFieldConfig): FormlyFieldConfig | null {
    if (!field) {
      return null;
    }
    if (
      field.get!('operationtype') &&
      field.get!('operationtype').key == 'operationtype'
    ) {
      return field;
    }
    return this.getFieldOperationType(field.parent!);
  }

  isInsertOperation(field: any): boolean {
    return field.get('operationtype').formControl!.getRawValue() == 1;
  }

  isUpdateOperation(field: any): boolean {
    return field.get('operationtype').formControl!.getRawValue() == 2;
  }

  isNullifyOperation(field: any): boolean {
    return field.get('operationtype').formControl!.getRawValue() == 4;
  }

  isInvalidateOperation(field: any): boolean {
    return field.get('operationtype').formControl!.getRawValue() == 6;
  }

  lookupValidator = {
    expression: (control: AbstractControl, field: FormlyFieldConfig) => {

      this.injector.get(LoggerService).debug(`Lookup Validating field [${field.key}]`)

      return (
        !control.value ||
        (field.parent &&
          field.parent.model &&
          field.parent.model.name &&
          field.parent.model.name != '')
      );
    },
    message: (_error: any, field: FormlyFieldConfig) =>
      `The reference ${field.formControl?.value} doesn't exist in the current environment`,
  };

  lookupSenderIdValidator = {
    expression: (control: AbstractControl, field: FormlyFieldConfig) => {

      this.injector.get(LoggerService).debug(`Sender Id Validating field [${field.key}]`)

      let result: any = null;

      if (control.value) {
        result = this.injector
          .get(LookupGetService)
          .getLookupValueSync(
            'senderid',
            control.value
          );
        return (result &&
          result.name != '');
      } else {
        return true;
      }

    },
    message: (_error: any, field: FormlyFieldConfig) =>
      `The reference ${field.formControl?.value} doesn't exist in the current environment`,
  };

  lookupMeddraValidator = {
    expression: (control: AbstractControl, field: FormlyFieldConfig) => {

      this.injector.get(LoggerService).debug(`Meddra Validating field [${field.key}]`)

      //Meddra level control
      const meddraLevelControl =
        field.parent!.parent!.parent!.get!('meddralevel').formControl;

      //Meddra version control
      const meddraVersionControl =
        field.parent!.parent!.parent!.get!('meddraversion').formControl;

      //Meddra code value control
      const meddraValueControl = field.formControl;

      //if name exists it is not needed any validation
      if (field && field.parent && field.parent.model && field.parent.model.name) return true;

      if (
        meddraLevelControl!.value &&
        meddraValueControl!.value &&
        meddraVersionControl!.value
      ) {
        //Get remote value
        let result: CodeName = this.injector
          .get(LookupGetService)
          .getLookupValueSync(
            meddraLevelControl!.value,
            meddraValueControl!.value + '_v' + meddraVersionControl!.value
          );

        if (field.parent && field.parent.model) {
          field.parent!.model!.name = result.name;
        }
        field.props!['lookup'].name = meddraLevelControl!.value;

        return result.name != '';
      } else {
        if (field && field.parent && field.parent.model)
          field.parent.model.name = '';
        return true;
      }
    },
    message: (_error: any, field: FormlyFieldConfig) =>
      `The reference ${field.formControl?.value} doesn't exist in the current environment`,
  };

  inOptionsDatamasked = {
    expression: (control: AbstractControl, field: FormlyFieldConfig) => { // JMBL
      if (this.injector.get(AuthService).isDatamaskedUser() && field.model && field.model.roleofingredient == 2 &&
        field.model.concentrationtypecode && field.model.concentrationtypecode.value == 0) {
        return of({ inOptions: false }); // valid
      } else {
        return this.inOptions.expression(control, field);
      }
    }
  }

  meddraLevelPTLLTValidator = {
    expression: (control: AbstractControl, field: FormlyFieldConfig) => {
      if (control.value == undefined || control.value === '') {
        return !field.props?.required;
      }

      const levelName = this.injector
        .get(LookupGetService)
        .retrieveValueFromCache('meddraLevel', control.value);

      return this.isPtOrLltMeddraLevel(control.value);
    },
    message: () => 'Only Preferred Term (PT) or Low Level Term (LLT) can be selected.',
  };


  inOptions = {
    expression: (control: AbstractControl, field: FormlyFieldConfig) => {

      this.injector.get(LoggerService).debug(`Validating field [${field.key}]`)

      const options$ = field.props?.['options']; // Ensure options exist

      //if it has no value or it is datamasked
      if ((!control.value) || (control.value == 0)) {
        return of({ inOptions: false }); // valid
      }
      if (options$ instanceof Observable) {
        return options$.pipe(
          map((options: any[]) => {
            const foundOption = options.find(
              (option) => option.code == control.value
            ); // Find the matching option
            const existsOption = foundOption; // Check if the option exists
            return existsOption ? { inOptions: false } : null; // Return validation status
          })
        );
      } else if (options$ instanceof Array) {
        const foundOption = options$.find(
          (option) => option.value === control.value
        ); // Find the matching option
        const existsOption = foundOption; // Check if the option exists
        return existsOption ? of({ inOptions: false }) : of(null); // Return validation status
      }

      return of({ inOptions: false }); // valid
    },
    message: () =>
      'Invalid selection, please choose one from available options.',
  };

  AP_12_12_BR_5 = {
    expression: (control: AbstractControl, field: FormlyFieldConfig) => {
      const authorisationStatus = field.parent!.parent!.get!('authorisationstatus');
      const authorisationStatusValue = authorisationStatus.formControl!.value['value'];
      if (authorisationStatusValue && ![1, 8, 9, 12].includes(authorisationStatusValue)) {
        if (!field.formControl!.value) {
          return false;
        } else {
          return true;
        }
      }
      return true;
    },
    message: (_error: any, field: FormlyFieldConfig) =>
      `Business Rule AP.12.12.BR.5 Violated: If the value of authorisation status signifies an MA value 2, 3, 4, 5, 6, 7, 10 or 11, then invalidated date (AP.12.12) must be provided.`,
  };

  AP_12_12_BR_6 = {
    expression: (control: AbstractControl, field: FormlyFieldConfig) => {
      const authorisationStatus = field.parent!.parent!.get!('authorisationstatus');
      const authorisationStatusValue = authorisationStatus.formControl!.value['value'];
      if (authorisationStatusValue && [1, 8, 9, 12].includes(authorisationStatusValue)) {
        if (field.formControl!.value) {
          return false;
        } else {
          return true;
        }
      }
      return true;
    },
    message: (_error: any, field: FormlyFieldConfig) =>
      `Business Rule AP.12.12.BR.6 Violated: If the value of authorisation status signifies an MA value 1, 8, 9 or 12, then invalidated date (AP.12.12) must be empty.`,
  };

  //US-325380
  AP_PPI_2_BR = {
    expression: (control: AbstractControl, field: FormlyFieldConfig) => {
      //Check whether "operationtype" is an insert (1) or update (2)
      const operationtype = field.props!['operationtype'];
      if (operationtype && (operationtype === 1 || operationtype === 2)) {
        //Check whether “resolutionmode” is a lookup (2) and the formControl does not contain a value
        const resolutionmode = field.parent!.parent!.get!('attachmentcode').formControl!.value['resolutionmode'];
        const vdValue = control.value;
        if (resolutionmode && resolutionmode === 2 && !vdValue) {
          return false;
        }
      }
      return true;
    },
    message: (_error: any, field: FormlyFieldConfig) =>
      'Field is mandatory / must have a specified value',
  }

  medicinalProductTypeFields: FormlyFieldConfig[] = [
    {
      key: 'producttypecode',
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          key: 'id',
          props: {
            readonly: true,
          },
        },
        {
          key: 'value',
          type: 'selectType',
          className: 'flex-1',
          props: {
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('medicinalproducttype'),
            valueProp: 'code',
            labelProp: 'name',
            label: 'Medicinal Product Type',
            required: true,
            attributes: {
              title:
                'The applicable type(s) of the medicinal product must be specified in this section as per guidance described for this field in Chapter 3.II. (AP.12.MPT.1)',
            },
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
          hooks: {
            onInit: (field) => {
              field.formControl!.valueChanges.subscribe(value => {
                const selected = this.injector.get(LookupGetService).retrieveValueFromCache("medicinalproducttype", value);
                if (selected) {
                  field.form!.get('name')?.setValue(selected);
                }
              });
            }
          }
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
  ];

  messageHeaderFields: FormlyFieldConfig[] = [
    {
      key: 'ichicsrmessageheader',
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          key: 'messagenumb',
          defaultValue: '',
          className: 'flex-1',
          type: 'input',
          props: {
            label: 'Message Number',
            required: true,
            maxLength: 100,
            attributes: {
              title:
                'The Message Number will be Pre-pended with Product EV Code and Version when the Message contains a MAH Owned Product Update',
            },
          },
        },
      ],
    },
  ];

  authorisedSenderFields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          key: 'version'
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          key: 'versionDate'
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          key: 'versionBy'
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          key: 'stamp_nullif'
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          key: 'newVersionDate'
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'ev_Code',
          defaultValue: '',
          type: 'input',
          props: {
            label: 'EV Code',
            required: true,
            readonly: true,
            disabled: true,
            attributes: {
              title: 'EV Code',
            },
          },
          hideExpression: (model, formState, field) => {
            return field!.parent!.model.operationtype === 1;
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'operationtype',
          defaultValue: 1,
          type: 'select',
          props: {
            label: 'Operation Type',
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('operationtype'),
            valueProp: 'code',
            labelProp: 'name',
            required: true,
            readonly: true,
            disabled: true,
            attributes: {
              title: 'Operation Type',
            },
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'type',
          defaultValue: 2,
          type: 'selectType',
          props: {
            label: 'Type',
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('productstypes'),
            valueProp: 'code',
            labelProp: 'name',
            required: true,
            readonly: true,
            disabled: true,
            attributes: {
              title: 'Type',
            },
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      key: 'localnumber',
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'mahcode',
          fieldGroup: [
            {
              key: 'value',
              type: 'input',
              wrappers: ['modal-wrapper'],
              props: {
                label: 'MAH',
                required: true,

                lookup: {
                  name: 'organisation',
                  parameters: [['type', 1]],
                  description: 'Select MAH Sender ID',
                  columns: [
                    new ListColumn('Num', ListDataColumn.TEXT, 'num'),
                    new ListColumn('EV Code', ListDataColumn.TEXT, 'code'),
                    new ListColumn('MAH Name', ListDataColumn.TEXT, 'name'),
                    new ListColumn(
                      'Validated',
                      ListDataColumn.DATE,
                      'validated'
                    ),
                  ],
                  type: 'LR',
                },
                readonly: true,
                attributes: {
                  title:
                    'The MAH corresponding to the legal entity of the medicinal product in the given country must be specified. (AP.4)',
                },
              },
              validators: {
                lookupValidator: this.lookupValidator,
              },
            },
            {
              key: 'resolutionmode',
            },
            {
              key: 'name',
            },
          ],
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          fieldGroup: [
            {
              key: 'qppvcode',
              fieldGroup: [
                {
                  key: 'value',
                  type: 'masked-input',
                  wrappers: ['modal-wrapper'],
                  props: {
                    maskChar: '*',
                    maskIf: (field: any) => this.injector.get(AuthService).isDatamaskedUser() && !!field.formControl?.value,
                    label: 'QPPV',
                    lookup: {
                      name: 'qppv',
                      description: 'Select QPPV',
                      columns: [
                        new ListColumn('Num', ListDataColumn.TEXT, 'num'),
                        new ListColumn(
                          'QPPV Code',
                          ListDataColumn.TEXT,
                          'code'
                        ),
                        new ListColumn(
                          'Full Name',
                          ListDataColumn.TEXT,
                          'name'
                        ),
                        new ListColumn(
                          'First Name',
                          ListDataColumn.TEXT,
                          'firstName'
                        ),
                        new ListColumn(
                          'Family Name',
                          ListDataColumn.TEXT,
                          'familyName'
                        ),
                        new ListColumn(
                          'Organisation ID',
                          ListDataColumn.TEXT,
                          'organisationID'
                        ),
                        new ListColumn(
                          'Organisation Name',
                          ListDataColumn.TEXT,
                          'organisation'
                        ),
                        new ListColumn(
                          'EV Profile Type',
                          ListDataColumn.TEXT,
                          'evprofile'),
                        new ListColumn('Role', ListDataColumn.TEXT, 'role'),
                        new ListColumn(
                          'Responsible',
                          ListDataColumn.TEXT,
                          'responsible'
                        ),
                        new ListColumn(
                          'Enabled',
                          ListDataColumn.TEXT,
                          'enabled'
                        ),
                      ],
                      type: 'R',
                    },
                    required: true,
                    readonly: true,
                    attributes: {
                      title:
                        'The EU QPPV or national QPPV responsible for the authorised medicinal product must be specified. (AP.5)',
                    },
                  },
                  expressions: {
                    'props.required': (field: FormlyFieldConfig) => {
                      return !this.injector.get(AuthService).isDatamaskedUser();
                    },
                    'props.disabled': (field: FormlyFieldConfig) => {
                      return (field.formControl?.value == '0' && field.parent?.parent?.model?.operationtype != 1) || this.injector.get(AuthService).isDatamaskedUser();
                    },
                  },
                  validators: {
                    lookupValidator: this.lookupValidator,
                  },
                },
                {
                  key: 'name',
                },
                {
                  key: 'resolutionmode',
                },
              ],
            },
          ],
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          fieldGroup: [
            {
              key: 'mflcode',
              fieldGroup: [
                {
                  key: 'value',
                  type: 'input',
                  wrappers: ['modal-wrapper'],
                  props: {
                    label: 'Master File Location',
                    lookup: {
                      name: 'mfl',
                      description: 'Select MFL',
                      type: 'LR',
                      columns: [
                        new ListColumn('Num', ListDataColumn.TEXT, 'num'),
                        new ListColumn('EV Code', ListDataColumn.TEXT, 'code'),
                        new ListColumn('Name', ListDataColumn.TEXT, 'name'),
                        new ListColumn('Address', ListDataColumn.TEXT, 'street'),
                        new ListColumn(
                          'Country',
                          ListDataColumn.TEXT,
                          'country'
                        ),
                        new ListColumn(
                          'PostCode',
                          ListDataColumn.TEXT,
                          'postcode'
                        ),
                        new ListColumn('State', ListDataColumn.TEXT, 'state'),
                        new ListColumn('City', ListDataColumn.TEXT, 'city'),
                        new ListColumn(
                          'Building',
                          ListDataColumn.TEXT,
                          'building'
                        ),
                        new ListColumn(
                          'Company Name',
                          ListDataColumn.TEXT,
                          'companyname'
                        ),
                      ],
                    },
                    required: true,
                    readonly: true,
                    disabled: this.injector.get(AuthService).isDatamaskedUser(),

                    attributes: {
                      title:
                        'The location where the PSMF for the authorised medicinal product is stored must be specified. (AP.6)',
                    },
                  },
                  expressions: {
                    'props.required': (field: FormlyFieldConfig) => {
                      return !this.injector.get(AuthService).isDatamaskedUser();
                    },
                  },
                  validators: {
                    lookupValidator: this.lookupValidator,
                  },
                },
                {
                  key: 'resolutionmode',
                },
                {
                  key: 'name',
                },
              ],
            },
          ],
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'enquiryemail',
          type: 'input',
          props: {
            label: 'PhV enquiry email',
            attributes: {
              autocomplete: 'PLEASE_DO_NOT_AUTOCOMPLETE',
              title:
                'The email address where enquiries related to Pharmacovigilance can be submitted must be specified. (AP.7)',
            },
          },
          validators: {
            customValidator: {
              expression: (c: any) =>
                /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
                  c.value
                ),
              message: (_error: any, field: FormlyFieldConfig) =>
                `${field.formControl?.value ?? "''"
                } is not a valid email address or blank space is present`,
            },
          },
          expressions: {
            'props.required': (field: FormlyFieldConfig) => {
              return this.isInsertOperation(this.getFieldOperationType(field));
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'enquiryphone',
          type: 'input',
          props: {
            label: 'PhV enquiry phone',
            required: true,
            attributes: {
              autocomplete: 'PLEASE_DO_NOT_AUTOCOMPLETE',
              title:
                'The phone number where enquiries related to Pharmacovigilance can be directed must be specified. (AP.8)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'senderlocalcode',
          type: 'input',
          props: {
            label: 'Sender Local Code',
            required: false,

            attributes: {
              title:
                'Local code of the AMP used by the MAH for internal reference purposes may be specified. (AP.9)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'infodateview',
          type: 'datetime',
          props: {
            label: 'Info Date',
            keys: ['infodateformat', 'infodate'],
            required: false,
            showFormat: false,
            attributes: {
              title:
                'The date when the lifting of suspension of the marketing authorisation becomes effective must be specified when the authorisation status changes from' +
                ' Valid - Suspended' +
                ' to any' +
                ' Valid status.' +
                ' (AP.11)',
              // TODO: BUSINESS RULE: FOR UPDATE, WHEN THE AUTHORISATION STATUS CHANGES FROM Valid – Suspended (2) TO 1, 8 OR 9.
            },
          },
          validators: {
            customValidator: {
              expression: (control: any) => {
                let value = new Date();
                let today = new Date();
                if (control.value) {
                  if (
                    control.parent.controls.infodateformat.value ==
                    '102'
                  ) {
                    const [dd, mm, yyyy] = control.value
                      .split('/')
                      .map(Number);
                    value = new Date(yyyy, mm - 1, dd);
                    today = new Date();
                  } else {
                    const [mm, yyyy] = control.value.split('/').map(Number);
                    value = new Date(yyyy, mm - 1);
                    today = new Date();
                  }
                }

                return control.value == undefined || value <= today;
              },
              message: () => 'Date cannot be higher than today',
            },
          },
        },
        {
          key: 'infodateformat',
        },
        {
          key: 'infodate',
          expressionProperties: {
            'model.infodateview': (model) =>
              model && model.infodateview === undefined
                ? DateUtil.importDate(model.infodate, model.infodateformat)
                : model == undefined || model.infodateview === undefined
                  ? undefined
                  : model.infodateview,
          },
        },
      ],
    },
  ];

  developmentSenderFields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'ev_Code',
          defaultValue: '',
          type: 'input',
          props: {
            label: 'EV Code',
            required: true,
            readonly: true,
            disabled: true,
            attributes: {
              title: 'EV Code',
            },
          },
          hideExpression: (model, formState, field) => {
            return field!.parent!.model.operationtype === 1;
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'operationtype',
          defaultValue: 1,
          type: 'select',
          props: {
            label: 'Operation Type',
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('operationtype'),
            valueProp: 'code',
            labelProp: 'name',
            required: true,
            readonly: true,
            disabled: true,
            attributes: {
              title: 'Operation Type',
            },
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'type',
          defaultValue: 1,
          type: 'selectType',
          props: {
            label: 'Type',
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('productstypes'),
            valueProp: 'code',
            labelProp: 'name',
            required: true,
            readonly: true,
            disabled: true,
            attributes: {
              title: 'Type',
            },
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      key: 'localnumber',
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          key: 'newownerid',
          className: 'flex-1',
          type: 'input',
          wrappers: ['modal-wrapper'],
          hideExpression: () => {
            return !this.injector.get(AuthService).isEMAOrganisation();
          },
          props: {
            label: 'Owner ID',
            required: true,

            placeholder: '',
            lookup: {
              name: 'senderid',
              description: 'Select Owner',
              type: 'R',
              remoteMode: 'cache',
              columns: [
                new ListColumn('Num', ListDataColumn.TEXT, 'num'),
                new ListColumn('EV ID', ListDataColumn.TEXT, 'code'),
                new ListColumn('Name', ListDataColumn.TEXT, 'name'),
                new ListColumn('Country', ListDataColumn.TEXT, 'countryname'),
                new ListColumn('Connection', ListDataColumn.TEXT, 'trading'),
                new ListColumn(
                  'Type',
                  ListDataColumn.TEXT,
                  'organisationevtype'
                ),
                new ListColumn(
                  'EV Profile Type',
                  ListDataColumn.TEXT,
                  'evprofile'
                ),
              ],
            },
            readonly: true,
            attributes: {
              title:
                'Select the Owner for the Development Product (Insert operation type only) (DP.3)',
            },
          },
          validators: {
            lookupValidator: this.lookupSenderIdValidator,
          },
          expressions: {
            'props.required': (field: FormlyFieldConfig) => {
              return (
                this.injector.get(AuthService).isEMAOrganisation() &&
                field.parent!.parent!.get!(
                  'operationtype'
                ).formControl!.getRawValue() == 1
              );
            },
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'senderlocalcode',
          type: 'input',
          props: {
            label: 'Sender Local Code',
            required: false,

            attributes: {
              title: 'Sender Local Code (DP.4)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          fieldGroup: [
            {
              key: 'sponsorcode',
              fieldGroup: [
                {
                  key: 'value',
                  type: 'input',
                  wrappers: ['modal-wrapper'],
                  props: {
                    label: 'Sponsor',
                    placeholder: '',
                    required: true,

                    lookup: {
                      name: 'organisation',
                      description: 'Select Sponsor',
                      parameters: [['type', 2]],
                      columns: [
                        new ListColumn('Num', ListDataColumn.TEXT, 'num'),
                        new ListColumn('EV Code', ListDataColumn.TEXT, 'code'),
                        new ListColumn(
                          'Sponsor Name',
                          ListDataColumn.TEXT,
                          'name'
                        ),
                        new ListColumn(
                          'Validated',
                          ListDataColumn.DATE,
                          'validated'
                        ),
                      ],
                      type: 'LR',
                    },
                    readonly: true,
                    attributes: {
                      title: 'Sponsor (DP.5)',
                    },
                  },
                  validators: {
                    lookupValidator: this.lookupValidator,
                  },
                },
                {
                  key: 'resolutionmode',
                },
                {
                  key: 'name',
                },
              ],
            },
          ],
        },
      ],
    },
  ];

  authorisationDetailFields: FormlyFieldConfig[] = [
    {
      key: 'authorisation',
      fieldGroup: [
        {
          fieldGroupClassName: 'display-flex',
          fieldGroup: [
            {
              className: 'flex-1',
              key: 'authorisationprocedure',
              fieldGroup: [
                {
                  key: 'value',
                  type: 'selectType',
                  props: {
                    options: this.injector
                      .get(LookupGetService)
                      .loadCachedOptions('authorisationprocedure'),
                    label: 'Authorisation Procedure',
                    required: true,
                    valueProp: 'code',
                    labelProp: 'name',
                    attributes: {
                      title:
                        'The procedure through which the medicinal product was authorised in the referenced country must be specified. (AP.12.2)',
                      onkeydown: 'copyValueToClipboard(event,this.value)',
                    },
                  },
                  asyncValidators: {
                    inOptions: this.inOptions,
                  },
                  validators: {
                    customValidator: {
                      expression: (control: any, field: FormlyFieldConfig) => {
                        const legalbasis =
                          control.root.value.authorisation.legalbasis.value;
                        return legalbasis == undefined || control.value == undefined ||
                          this.isValidAuthrisedProcedureLegalBasis(control.value, legalbasis)
                      },
                      message: () =>
                        'Authorisation procedure is not compatible with Legal Basis',
                    },
                  },
                },
                {
                  key: 'name',
                },
                {
                  key: 'resolutionmode',
                },
              ],
            },
          ],
        },
        {
          fieldGroupClassName: 'display-flex',
          fieldGroup: [
            {
              className: 'flex-1',
              fieldGroup: [
                {
                  key: 'authorisationcountrycode',
                  fieldGroup: [
                    {
                      key: 'value',
                      type: 'selectType',
                      props: {
                        label: 'Authorisation Country Code',
                        required: true,

                        options: this.injector
                          .get(LookupGetService)
                          .loadCachedOptions('country'),
                        valueProp: 'code',
                        labelProp: 'name',
                        attributes: {
                          title:
                            'The country where the medicinal product is authorised must be specified. (AP.12.1)',
                        },
                      },
                      parsers: [
                        (value: string) => value ? value.toUpperCase() : value
                      ],
                      asyncValidators: {
                        inOptions: this.inOptions,
                      },
                      hooks: {
                        onInit: (field) => {
                          let previousValue: any;
                          const authorisationprocedureFormControl =
                            field.parent!.parent!.parent!.parent!.fieldGroup![0]
                              .fieldGroup![0].fieldGroup![0].formControl;
                          authorisationprocedureFormControl!.valueChanges.subscribe(
                            (value) => {
                              if (value != previousValue) {
                                let lookupToLoad = 'countryeea';
                                if (value == 1) {
                                  lookupToLoad = 'countryeu';
                                } else if (value == 5) {
                                  lookupToLoad = 'countrynoneea';
                                }

                                field.props!['options'] = this.injector
                                  .get(LookupGetService)
                                  .loadCachedOptions(lookupToLoad);

                                let valueFound =
                                  field.formControl?.value == null ||
                                    this.injector
                                      .get(LookupGetService)
                                      .retrieveValueFromCache(
                                        lookupToLoad,
                                        field.formControl?.value
                                      ) == ''
                                    ? false
                                    : true;
                                if (valueFound) {
                                  field.formControl?.setValue(
                                    field.formControl.value
                                  );
                                } else {
                                  field.formControl?.setValue(null);
                                }
                                previousValue = value;
                              }
                            }
                          );
                        },
                      },
                    },
                    {
                      key: 'name',
                    },
                    {
                      key: 'resolutionmode',
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          fieldGroupClassName: 'display-flex',
          fieldGroup: [
            {
              className: 'flex-1',
              fieldGroup: [
                {
                  key: 'authorisationstatus',
                  fieldGroup: [
                    {
                      key: 'value',
                      type: 'selectType',
                      props: {
                        options: this.injector
                          .get(LookupGetService)
                          .loadCachedOptions('authorisationstatus'),
                        valueProp: 'code',
                        labelProp: 'name',
                        label: 'Authorisation Status',
                        required: true,
                        attributes: {
                          title:
                            'The status of the marketing authorisation of the medicinal product must be specified. (AP.12.3)',
                        },
                      },
                      asyncValidators: {
                        inOptions: this.inOptions,
                      },
                      validators: {
                        customValidator: {
                          expression: (
                            control: AbstractControl,
                            field: FormlyFieldConfig
                          ) => {
                            if (
                              !this.injector
                                .get(Art57Section)
                                .isCreateAndSendProductSection()
                            ) {
                              return true;
                            } else if (field.formControl?.value == null) {
                              return !field.props!['required'];
                            }

                            const operationTypeField =
                              field.parent!.parent!.parent!.parent!.parent!;
                            let lookUpValueSet = '';
                            if (this.isInsertOperation(operationTypeField)) {
                              lookUpValueSet = 'validauthorisationstatus';
                            } else if (
                              this.isInvalidateOperation(operationTypeField)
                            ) {
                              lookUpValueSet = 'notvalidauthorisationstatus';
                            } else {
                              lookUpValueSet = 'authorisationstatus';
                            }
                            field.props!['options'] = this.injector
                              .get(LookupGetService)
                              .loadCachedOptions(lookUpValueSet);

                            let valueExistsInLookup: boolean =
                              field.formControl == undefined ||
                                field.formControl?.value == null ||
                                this.injector
                                  .get(LookupGetService)
                                  .retrieveValueFromCache(
                                    lookUpValueSet,
                                    field.formControl.value
                                  ) == ''
                                ? false
                                : true;

                            if (!valueExistsInLookup) {
                              field.formControl?.setValue(null);
                            }

                            return valueExistsInLookup;
                          },
                          message: 'El valor debe ser una opción válida.',
                        },
                      },
                      hooks: {
                        onInit: (field) => {
                          let lookUpValueSet = '';
                          const operationTypeField =
                            field.parent!.parent!.parent!.parent!.parent!;
                          if (
                            this.injector
                              .get(Art57Section)
                              .isCreateAndSendProductSection() &&
                            operationTypeField.formControl!
                          ) {
                            if (this.isInsertOperation(operationTypeField)) {
                              lookUpValueSet = 'validauthorisationstatus';
                            } else if (
                              this.isInvalidateOperation(operationTypeField)
                            ) {
                              lookUpValueSet = 'notvalidauthorisationstatus';
                            } else {
                              lookUpValueSet = 'authorisationstatus';
                            }
                            field.props!['options'] = this.injector
                              .get(LookupGetService)
                              .loadCachedOptions(lookUpValueSet);
                          }
                        },
                      },
                    },
                    {
                      key: 'name',
                    },
                    {
                      key: 'resolutionmode',
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          fieldGroupClassName: 'display-flex',
          fieldGroup: [
            {
              className: 'flex-1',
              key: 'authorisationnumber',
              type: 'input',
              props: {
                label: 'Authorisation Number',
                required: true,

                attributes: {
                  title:
                    'The number assigned by the competent authority that granted the marketing authorisation for the medicinal product must be specified. (AP.12.4)',
                },
              },
            },
          ],
        },
        {
          fieldGroupClassName: 'display-flex',
          fieldGroup: [
            {
              className: 'flex-1',
              key: 'authorisationdateview',
              type: 'datetime',
              props: {
                label: 'Authorisation/Renewal Date',
                keys: ['authorisationdateformat', 'authorisationdate'],
                required: true,
                showFormat: true,
                attributes: {
                  title:
                    'The date when the first authorisation, or the date when the authorisation renewal was granted (whichever is the latest) by the authorising competent authority must be specified.  (AP.12.5)',
                },
              },
            },
            {
              key: 'authorisationdateformat',
            },
            {
              key: 'authorisationdate',
              expressionProperties: {
                'model.authorisationdateview': (model) =>
                  model && model.authorisationdateview === undefined
                    ? DateUtil.importDate(
                      model.authorisationdate,
                      model.authorisationdateformat
                    )
                    : model == undefined ||
                      model.authorisationdateview === undefined
                      ? undefined
                      : model.authorisationdateview,
              },
            },
          ],
        },
        {
          fieldGroupClassName: 'display-flex',
          fieldGroup: [
            {
              className: 'flex-1',
              key: 'mrpnumber',
              type: 'input',
              props: {
                label: 'MRP/DCP/EMEA Number',
                required: false,

                attributes: {
                  title:
                    'MRP/DCP/EMEA procedure number must be specified as applicable, depending on the selected authorisation procedure. (AP.12.7)',
                },
              },
              validators: {
                customValidator: {
                  expression: (
                    control: AbstractControl,
                    field: FormlyFieldConfig
                  ) => {
                    if (
                      field.parent?.model &&
                      field.parent?.model.authorisationprocedure
                    ) {
                      if (
                        field.parent?.model.authorisationprocedure.value == 1 ||
                        field.parent?.model.authorisationprocedure.value == 3 ||
                        field.parent?.model.authorisationprocedure.value == 7
                      ) {
                        field.props!['required'] = true;
                      } else {
                        field.props!['required'] = false;
                      }
                    }
                    return true;
                  },
                },
              },
            },
          ],
        },
        {
          fieldGroupClassName: 'display-flex',
          fieldGroup: [
            {
              className: 'flex-1',
              key: 'eunumber',
              type: 'input',
              props: {
                label: 'EU Number',
                required: false,

                attributes: {
                  title:
                    'The marketing authorisation number assigned by the European Commission to the medicinal product approved under the centralised procedure must be specified. (AP.12.8)',
                },
              },
              validators: {
                customValidator: {
                  expression: (
                    control: AbstractControl,
                    field: FormlyFieldConfig
                  ) => {
                    if (
                      field.parent?.model &&
                      field.parent?.model.authorisationprocedure
                    ) {
                      if (
                        field.parent?.model.authorisationprocedure.value == 1
                      ) {
                        field.props!['required'] = true;
                      } else {
                        field.props!['required'] = false;
                      }
                    }
                    return true;
                  },
                },
              },
            },
          ],
        },
        {
          fieldGroupClassName: 'display-flex',
          fieldGroup: [
            {
              className: 'flex-1',
              key: 'legalbasis',
              fieldGroup: [
                {
                  key: 'value',
                  type: 'selectType',
                  props: {
                    label: 'Legal Basis',
                    required: true,

                    options: this.injector
                      .get(LookupGetService)
                      .loadCachedOptions('legalbasis'),
                    valueProp: 'code',
                    labelProp: 'name',
                    attributes: {
                      title: 'Legal Basis (AP.12.13)',
                    },
                  },
                  asyncValidators: {
                    inOptions: this.inOptions,
                  },
                  validators: {
                    customValidator: {
                      expression: (control: any) => {
                        const authorisationprocedure =
                          control.root.value.authorisation
                            .authorisationprocedure.value;

                        return control.value == undefined || authorisationprocedure == undefined ||
                          this.isValidAuthrisedProcedureLegalBasis(authorisationprocedure, control.value);
                      },
                      message: () =>
                        'Authorisation procedure is not compatible with Legal Basis',
                    },
                  },
                },
                {
                  key: 'name',
                },
                {
                  key: 'resolutionmode',
                },
              ],
            },
          ],
        },
        {
          fieldGroupClassName: 'display-flex',
          fieldGroup: [
            {
              className: 'flex-1',
              key: 'orphandrug',
              fieldGroup: [
                {
                  key: 'value',
                  type: 'selectType',
                  props: {
                    label: 'Orphan Drug',
                    required: true,
                    options: this.injector
                      .get(LookupGetService)
                      .loadCachedOptions('orphanDrug'),
                    valueProp: 'code',
                    labelProp: 'name',
                    attributes: {
                      title:
                        'The value indicating whether the AMP is considered an orphan drug medicine or not must be specified. (AP.12.9)',
                    },
                    // TODO: BUSINESS RULE, FIELD IS MANDATORY UNLESS OPERATION TYPE IS NULLIFICATION (4) OR INVALIDATE (6)
                  },
                  asyncValidators: {
                    inOptions: this.inOptions,
                  },
                },
                {
                  key: 'name',
                },
                {
                  key: 'resolutionmode',
                },
              ],
            },
          ],
        },
        {
          fieldGroupClassName: 'display-flex',
          fieldGroup: [
            {
              className: 'flex-1',
              key: 'intensivemonitoring',
              fieldGroup: [
                {
                  key: 'value',
                  type: 'selectType',
                  props: {
                    label: 'Additional Monitoring',
                    required: false,
                    options: this.injector
                      .get(LookupGetService)
                      .loadCachedOptions('additionalMonitoring'),
                    valueProp: 'code',
                    labelProp: 'name',
                    attributes: {
                      title:
                        'The value indicating whether the AMP is subject to additional monitoring or not must be specified. (AP.12.10)',
                    },
                  },
                  asyncValidators: {
                    inOptions: this.inOptions,
                  },
                },
                {
                  key: 'name',
                },
                {
                  key: 'resolutionmode',
                },
              ],
            },
          ],
        },
        {
          fieldGroupClassName: 'display-flex',
          fieldGroup: [
            {
              className: 'flex-1',
              key: 'withdrawndateview',
              type: 'datetime',
              props: {
                label: 'Invalidated Date',
                keys: ['withdrawndateformat', 'withdrawndate'],
                attributes: {
                  title:
                    'The date as of when the not-valid or valid-suspended authorisation status of the authorised medicinal product becomes effective must be specified. (AP.12.12)',
                },
              },
              validators: {
                customValidator: {
                  expression: (
                    control: AbstractControl,
                    field: FormlyFieldConfig
                  ) => {
                    let value = new Date();
                    let today = new Date();
                    if (control.value) {
                      const [dd, mm, yyyy] = control.value
                        .split('/')
                        .map(Number);
                      value = new Date(yyyy, mm - 1, dd);
                      today = new Date();
                    }

                    return control.value == undefined || value <= today;
                  },
                  message: () => 'Date cannot be higher than today',
                },
                BR_5: this.AP_12_12_BR_5,
                BR_6: this.AP_12_12_BR_6
              },
              expressionProperties: {
                'props.label': (
                  model: any,
                  formState: any,
                  field?: FormlyFieldConfig
                ) => {
                  let label = 'Invalidated date (AP.12.12)';
                  return field?.props?.required ? label : label;
                },
              },
            },
            {
              key: 'withdrawndateformat',
            },
            {
              key: 'withdrawndate',
              expressionProperties: {
                'model.withdrawndateview': (model) =>
                  model && model.withdrawndateview === undefined
                    ? DateUtil.importDate(
                      model.withdrawndate,
                      model.withdrawndateformat
                    )
                    : model == undefined ||
                      model.withdrawndateview === undefined
                      ? undefined
                      : model.withdrawndateview,
              },
            },
          ],
        },
      ],
    },
  ];

  authorisedPresentationNameFields: FormlyFieldConfig[] = [
    {
      key: 'presentationname',
      fieldGroup: [
        {
          fieldGroupClassName: 'display-flex',
          fieldGroup: [
            {
              className: 'flex-1',
              key: 'productname',
              type: 'textarea',
              props: {
                label: 'Full Presentation Name',
                required: true,
                attributes: {
                  title:
                    'The medicinal product name stated in Section 1. Name of the medicinal product of the corresponding SmPC must be specified. (AP.13.1)',
                },
              },
            },
          ],
        },
        {
          fieldGroupClassName: 'display-flex',
          fieldGroup: [
            {
              className: 'flex-1',
              key: 'productshortname',
              type: 'textarea',
              props: {
                label: 'Product Short Name',
                required: true,

                attributes: {
                  title:
                    'If included in the medicinal product name stated in Section 1. Name of the medicinal product of the SmPC, then the invented (trade) name must be specified as per guidance described for this field in Chapter 3.II. (AP.13.2)',
                },
              },
              validators: {
                customValidator: {
                  expression: (
                    control: AbstractControl,
                    field: FormlyFieldConfig
                  ) => {
                    const value =
                      field.formControl?.parent?.get(
                        'productgenericname'
                      )?.value;
                    field.props!['required'] = !value;
                    return true;
                  },
                },
              },
            },
          ],
        },
        {
          fieldGroupClassName: 'display-flex',
          fieldGroup: [
            {
              className: 'flex-1',
              key: 'productgenericname',
              type: 'textarea',
              props: {
                label: 'Product INN/Common Name',
                attributes: {
                  title:
                    'If included in the medicinal product name stated in Section 1. Name of the medicinal product of the SmPC, then the INN/common name/ scientific name must be specified as per guidance described for this field in Chapter 3.II. (AP.13.3)',
                },
              },
              validators: {
                customValidator: {
                  expression: (
                    control: AbstractControl,
                    field: FormlyFieldConfig
                  ) => {
                    const value =
                      field.formControl?.parent?.get('productshortname')?.value;
                    field.props!['required'] = !value;
                    return true;
                  },
                },
              },
            },
          ],
        },
        {
          fieldGroupClassName: 'display-flex',
          fieldGroup: [
            {
              className: 'flex-1',
              key: 'productcompanyname',
              type: 'textarea',
              props: {
                label: 'Product Company Name',
                optional: true,
                attributes: {
                  title:
                    'If included in the medicinal product name stated in Section 1. Name of the medicinal product of the SmPC, then the company name must be specified as per guidance described for this field in Chapter 3.II. If no company name is referenced in the medicinal product name stated in Section 1. Name of the medicinal product of the SmPC and no invented (trade) name is available for this product, the MAH name must be specified in this field as per guidance described for this field in Chapter 3.II. (AP.13.4)',
                },
              },
              validators: {
                customValidator: {
                  expression: (
                    control: AbstractControl,
                    field: FormlyFieldConfig
                  ) => {
                    const value =
                      field.formControl?.parent?.get('productshortname')?.value;
                    field.props!['required'] = !value;
                    return true;
                  },
                },
              },
            },
          ],
        },
        {
          fieldGroupClassName: 'display-flex',
          fieldGroup: [
            {
              className: 'flex-1',
              key: 'productstrength',
              type: 'textarea',
              props: {
                label: 'Product Strength Name',
                required: false,

                attributes: {
                  title:
                    'If included in the medicinal product name stated in Section 1. Name of the medicinal product of the SmPC, then the strength must be specified as per guidance described for this field in Chapter 3.II. (AP.13.5)',
                },
              },
            },
          ],
        },
        {
          fieldGroupClassName: 'display-flex',
          fieldGroup: [
            {
              className: 'flex-1',
              key: 'productform',
              type: 'textarea',
              props: {
                label: 'Product Form Name',
                attributes: {
                  title:
                    'If included in the medicinal product name stated in Section 1. Name of the medicinal product of the SmPC, then the pharmaceutical form name  must be specified as per guidance described for this field in Chapter 3.II. (AP.13.6)',
                },
              },
            },
          ],
        },
        {
          fieldGroupClassName: 'display-flex',
          fieldGroup: [
            {
              className: 'flex-1',
              key: 'packagedesc',
              type: 'textarea',
              props: {
                label: 'Package Description',
                rows: 5,
                attributes: {
                  title:
                    'Brief package description of the pack size corresponding to the referenced authorisation number as indicated in section 6.5 Nature and contents of container of the SmPC must be specified in the language of the SmPC or in English as per guidance described for this field in Chapter 3.II. (AP.13.7)',
                },
              },
            },
          ],
        },
      ],
    },
  ];

  commentFields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'comments',
          type: 'textarea',
          props: {
            label: 'Comment',
            rows: 5,
            attributes: {
              title:
                'If sections 4.1 Therapeutic indications or 4.2. Posology and method of administration of the SmPC indicate that the medicinal product is intended for pediatric population (children under the age of 18), then the text "Medicinal product authorised for the treatment in children" must be referenced in this field. If the product is being nullified, this field must reference also the reason for nullification.  (AP.14)',
            },
            maxLength: 500
          },
          expressions: {
            'props.required': (field: FormlyFieldConfig) => {
              return this.isNullifyOperation(this.getFieldOperationType(field));
            },
          },
        },
      ],
    },
  ];

  developmentPresentationNameFields: FormlyFieldConfig[] = [
    {
      key: 'developmentpresentationname',
      fieldGroup: [
        {
          fieldGroupClassName: 'display-flex',
          fieldGroup: [
            {
              className: 'flex-1',
              key: 'productcode',
              type: 'input',
              props: {
                label: 'Product Code',
                required: true,

                attributes: {
                  title: 'Code for the product (DP.6)',
                },
              },
              validators: {
                customValidator: {
                  expression: (
                    control: AbstractControl,
                    field: FormlyFieldConfig
                  ) => {
                    const value =
                      field.formControl?.parent?.get('productname')?.value;
                    field.props!['required'] = !value;
                    return true;
                  },
                },
              },
            },
          ],
        },
        {
          fieldGroupClassName: 'display-flex',
          fieldGroup: [
            {
              className: 'flex-1',
              key: 'productname',
              type: 'input',
              props: {
                required: true,

                label: 'Product Name',
                attributes: {
                  title: 'Name for the Product (DP.6)',
                },
              },
              validators: {
                customValidator: {
                  expression: (
                    control: AbstractControl,
                    field: FormlyFieldConfig
                  ) => {
                    const value =
                      field.formControl?.parent?.get('productcode')?.value;
                    field.props!['required'] = !value;
                    return true;
                  },
                },
              },
            },
          ],
        },
        {
          fieldGroupClassName: 'display-flex',
          fieldGroup: [
            {
              className: 'flex-1',
              key: 'productothername',
              type: 'input',
              props: {
                label: 'Product Other Name',
                required: false,

                attributes: {
                  title: 'Product Other Name (DP.6)',
                },
              },
            },
          ],
        },
      ],
    },

    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'comments',
          type: 'textarea',
          props: {
            label: 'Comment',
            rows: 5,
            maxLength: 500
          },
          expressions: {
            'props.required': (field: FormlyFieldConfig) => {
              return this.isNullifyOperation(this.getFieldOperationType(field));
            },
          },
        },
      ],
    },
  ];

  authorizedPharmaceuticalFormItemFields: FormlyFieldConfig[] = [
    {
      key: 'authpharmformcode',
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          key: 'value',
          type: 'input',
          wrappers: ['modal-wrapper'],
          className: 'flex-1',
          props: {
            label: 'Authorised Pharmaceutical Form',
            lookup: {
              name: 'pharmform',
              description: 'Select Pharmaceutical Form',
              type: 'LR',
              parameters: [['type', '2|3']],
              columns: [
                new ListColumn('Num', ListDataColumn.TEXT, 'num'),
                new ListColumn('EV Code', ListDataColumn.TEXT, 'code'),
                new ListColumn('Name', ListDataColumn.TEXT, 'name'),
                new ListColumn('Type', ListDataColumn.TEXT, 'type'),
              ],
            },
            required: true,

            readonly: true,
            attributes: {
              title:
                'The name(s) of the authorised pharmaceutical form(s) indicated in section 3. Pharmaceutical Form of the SmPC must be specified in this section as per guidance described for this field in Chapter 3.II. Standard term(s) of the authorised pharmaceutical form(s) must be referenced whenever possible. (AP.APF.1)',
            },
          },
          validators: {
            lookupValidator: this.lookupValidator,
          },
        },
        {
          key: 'resolutionmode',
        },
        {
          key: 'name',
        },
      ],
    },
  ];

  atcCodeItemFields: FormlyFieldConfig[] = [
    {
      key: 'atccode',
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'value',
          type: 'input',
          wrappers: ['modal-wrapper'],
          props: {
            label: 'ATC Code',
            lookup: {
              name: 'atc',
              description: 'Select ATC',
              type: 'LR',
              parameters: null,
              columns: [
                new ListColumn('Num', ListDataColumn.TEXT, 'num'),
                new ListColumn('EV Code', ListDataColumn.TEXT, 'code'),
                new ListColumn('Name', ListDataColumn.TEXT, 'name'),
                new ListColumn('Type', ListDataColumn.TEXT, 'type'),
              ],
            },
            required: true,

            readonly: true,
            localCode: 'atcs',
            showLocalButton: true,
            attributes: {
              title:
                'The ATC Code(s) as described in Section 5.1 Pharmacodynamic properties of the SmPC must be specified. Standard ATC Code(s) must be referenced whenever possible. (AP.ATC.1)',
            },
          },
          validators: {
            lookupValidator: this.lookupValidator,
          },
          expressions: {
            'props.lookup.parameters': (field: FormlyFieldConfig) => {
              if (
                this.injector.get(Art57Section).getSelectedSection()
                  .selectedNode.parent?.parent?.data.model.type == 2
              ) {
                //Authorised
                return [['type', '2|3']];
              } else {
                //Development
                //No filters needed
                return null;
              }
            },
          },
        },
        {
          key: 'resolutionmode',
        },
        {
          key: 'name',
        },
      ],
    },
  ];

  drugIngredientItemFields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'substancecode',
          fieldGroup: [
            {
              type: 'input',
              key: 'value',
              wrappers: ['modal-wrapper'],
              props: {
                label: 'Substance',
                lookup: {
                  name: 'substance',
                  description: 'Select Substance',
                  type: this.injector.get(AuthService).isEMAOrganisation() ? 'LR' : 'R',
                  parameters: null,
                  columns: [
                    new ListColumn('Num', ListDataColumn.TEXT, 'num'),
                    new ListColumn('EV Code', ListDataColumn.TEXT, 'code'),
                    new ListColumn(
                      'Substance Name',
                      ListDataColumn.TEXT,
                      'name'
                    ),
                    new ListColumn('Type', ListDataColumn.TEXT, 'type'),
                    new ListColumn('Language', ListDataColumn.TEXT, 'language'),
                    new ListColumn(
                      'Validated',
                      ListDataColumn.DATE,
                      'validated'
                    ),
                  ],
                },
                required: true,
                readonly: true,
                attributes: {
                  title:
                    'The substance name of the ingredient(s) indicated as active/excipient/adjuvant in line with the description of the ingredients referenced in the SmPC must be specified as per guidance described in Chapter 3.II. (PP.ACT.1/PP.EXC.1/PP.ADJ.1)',
                },
              },
              validators: {
                lookupValidator: this.lookupValidator,
              },
              expressions: {
                'props.disabled': (field: FormlyFieldConfig) => {
                  return field &&
                    field.parent?.parent &&
                    field.parent?.parent?.model.concentrationtypecode &&
                    field.parent?.parent?.model.concentrationtypecode.value == '0'
                },
                'props.lookup.parameters': (field: FormlyFieldConfig) => {
                  if (
                    this.injector.get(Art57Section).getSelectedSection()
                      .selectedNode.parent?.parent?.parent?.parent?.data.model
                      .type == 2
                  ) {
                    //Authorised
                    return [['type', 2]];
                  } else {
                    //Development
                    //No filters needed
                    return null;
                  }
                },
              },
            },
            {
              key: 'name',
            },
            {
              key: 'resolutionmode',
              defaultValue: 2,
            },
          ],
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'roleofingredient',
          type: 'radio',
          props: {
            label: 'Role of Ingredient',
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('roleofingredient'),
            valueProp: 'code',
            labelProp: 'name',
            disabled: false,
            required: true,
            attributes: {
              title: 'Role of Ingredient (PP.ACT/PP.EXC/PP.ADJ)',
            },
          },
          expressions: {
            'props.disabled': (field: FormlyFieldConfig) => {
              return field &&
                field.parent?.parent &&
                field.parent?.parent?.model.concentrationtypecode &&
                field.parent?.parent?.model.concentrationtypecode.value == '0'
            }
          }
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          key: 'ingredientStrengthInformation',
          className: 'flex-1',
          template:
            '<div><strong>Ingredient Strength Information<strong></div><br>',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'concentrationtypecode',
          fieldGroup: [
            {
              type: 'selectType',
              key: 'value',
              props: {
                options: this.injector
                  .get(LookupGetService)
                  .loadCachedOptions('amountvaluetype'),
                valueProp: 'code',
                labelProp: 'name',
                label: 'Amount Value Type',
                required: true,

                attributes: {
                  title:
                    "You can choose Equal, 'Not less than', 'Range', 'Up to' or 'Approximately' (PP.ACT.2/PP.EXC.2/PP.ADJ.2)",
                },
              },
              expressionProperties: {
                //This is needed for datamasking
                'props.options': (model, formState, field) => {
                  if ((field!.formControl?.value == '' && field!.formControl?.value != '0')) {
                    field!.formControl?.setValue(null);
                  }
                  if ((field!.formControl?.value == '0')) {
                    return [{ 'code': 0, 'name': '***' }]
                  } else {
                    return this.injector
                      .get(LookupGetService)
                      .loadCachedOptions('amountvaluetype');
                  }
                },
                'props.disabled': (model, formState, field) => {
                  return (field!.formControl?.value == '0');
                }
              },
              asyncValidators: {
                inOptions: this.inOptions,
              },
              validators: {
                customValidator: {
                  expression: (
                    control: AbstractControl,
                    field: FormlyFieldConfig
                  ) => {
                    if (
                      field.parent?.parent?.parent &&
                      field.parent.parent.parent.fieldGroup![1].fieldGroup![0]
                        .model.roleofingredient
                    ) {
                      field.props!['required'] = !(
                        field.parent.parent.parent.fieldGroup![1].fieldGroup![0]
                          .model.roleofingredient == 2
                      );
                    }

                    let lowAmountdenomunit =
                      field.parent!.parent!.parent!.fieldGroup![4].fieldGroup![2]
                        .fieldGroup![2].fieldGroup![0];

                    let highAmountdenomunit =
                      field.parent!.parent!.parent!.fieldGroup![5].fieldGroup![2]
                        .fieldGroup![2].fieldGroup![0];

                    highAmountdenomunit.formControl?.setValue(lowAmountdenomunit.formControl?.value);

                    return true;
                  },
                },
              },
              hooks: {
                onInit: (field) => {
                  const updateTemplate = () => {
                    const selectedOption = field.formControl?.value;

                    let newTemplate = '';
                    let newTemplateUpper = '';
                    const parentField = field?.parent?.parent?.parent;
                    const firstSectionLabelField =
                      parentField!.fieldGroup![4].get!('firstSectionLabel');
                    const secondSectionLabelField =
                      parentField!.fieldGroup![5].get!('secondSectionLabel');

                    switch (selectedOption) {
                      case 5:
                        newTemplate =
                          '<div><strong>Approximate Value</strong></div><br>';
                        break;
                      case 6:
                        newTemplate =
                          '<div><strong>Average Value</strong></div><br>';
                        break;
                      case 1:
                        newTemplate =
                          '<div><strong>Exact Value</strong></div><br>';
                        break;
                      case 4:
                        newTemplate =
                          '<div><strong>Minimum Value</strong></div><br>';
                        break;
                      case 2:
                        newTemplate =
                          '<div><strong>Low Limit</strong></div><br>';
                        newTemplateUpper =
                          '<div><strong>Upper Limit</strong></div><br>';
                        break;
                      case 3:
                        newTemplate =
                          '<div><strong>Maximum Value</strong></div><br>';
                        break;
                    }

                    if (firstSectionLabelField) {
                      firstSectionLabelField.template = newTemplate;
                    }

                    if (secondSectionLabelField) {
                      secondSectionLabelField.template = newTemplateUpper;
                    }
                  };

                  field.formControl?.valueChanges.subscribe(updateTemplate);
                  field.formControl?.setValue(field.formControl.value);
                },
              },
            },
            {
              key: 'name',
            },
            {
              key: 'resolutionmode',
            },
          ],
        },
        {
          className: 'flex-1',
          type: 'selectType',
          key: 'expressedAs',
          props: {
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('strength'),
            valueProp: 'code',
            labelProp: 'name',
            label: 'Expressed as',
            required: true,
          },
          expressionProperties: {
            'props.required': (model, formState, field) => {
              return !(model.concentrationtypecode &&
                model.concentrationtypecode.value == '0')
            },
            'props.options': (model, formState, field) => {
              if ((model.concentrationtypecode &&
                model.concentrationtypecode.value == '0')) {
                return [{ 'code': undefined, 'name': '***' }]
              } else {
                return field!.props!['options'];
              }
            },
            'props.disabled': (model, formState, field) => {
              return ((model.concentrationtypecode &&
                model.concentrationtypecode.value == '0'))
            }
          },
          hideExpression: (model, formState, field) => {
            return (
              field?.parent?.model.concentrationtypecode?.value == null ||
              field?.parent?.model.concentrationtypecode?.name === ''
            );
          },
          asyncValidators: {
            inOptions: this.inOptionsDatamasked,
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      key: 'lowAmountConditionalGroup',
      hideExpression: (model, formState, field) => {
        return (
          (field?.parent?.model.concentrationtypecode?.value == '' &&
            field?.parent?.model.concentrationtypecode?.value != 0
          ) ||
          field?.parent?.model.concentrationtypecode?.value == null
        );
      },
      // fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          fieldGroupClassName: 'display-flex',
          fieldGroup: [
            {
              key: 'firstSectionLabel',
              className: 'flex-1',
              template: '<div>space reserved</div>',
            },
          ],
        },
        {
          fieldGroupClassName: 'display-flex',
          fieldGroup: [
            {
              className: 'flex-1',
              type: 'masked-input',
              key: 'lowamountnumervalue',
              props: {
                label: 'Numerator Value',
                required: true,
                disabled: false,
                maskChar: '*',
                maskIf: (field: any) => field.parent?.parent?.parent?.model.concentrationtypecode.value == '0',
                attributes: {
                  title:
                    'The Low Limit Numerator Value or for non range measurements the Numerator Value (PP.ACT.3/PP.EXC.3/PP.ADJ.3)',
                },
              },
              expressionProperties: {
                //This is needed for datamasking
                'props.disabled': (model, formState, field) => {
                  return (field!.parent?.parent?.parent?.model.concentrationtypecode &&
                    field!.parent?.parent?.parent?.model.concentrationtypecode.value == '0');
                }
              },
              validators: {
                customValidator: {
                  expression: (c: any) => /^-?\d+(\.\d+)?$/.test(String(c.value).trim()),
                  message: () => 'Only numbers are allowed',
                },
              },
            },
            {
              className: 'flex-1',
              key: 'lowamountnumerprefix',
              fieldGroup: [
                {
                  type: 'selectType',
                  key: 'value',
                  props: {
                    options: this.injector
                      .get(LookupGetService)
                      .loadCachedOptions('amountprefix'),
                    label: 'Numerator Prefix',
                    required: true,
                    valueProp: 'code',
                    labelProp: 'name',
                    attributes: {
                      title:
                        'The Low Limit Numerator Prefix or for non range measurements the Numerator Prefix (PP.ACT.4/PP.EXC.4/PP.ADJ.4)',
                    },
                  },
                  expressionProperties: {
                    //This is needed for datamasking
                    'props.options': (model, formState, field) => {
                      if (field!.parent?.parent?.parent?.parent?.model.concentrationtypecode &&
                        field!.parent?.parent?.parent?.parent?.model.concentrationtypecode.value == '0') {
                        return [{ 'code': '0', 'name': '***' }]
                      } else {
                        return field!.props!['options'];
                      }
                    },
                    'props.disabled': (model, formState, field) => {
                      return (field!.parent?.parent?.parent?.parent?.model.concentrationtypecode &&
                        field!.parent?.parent?.parent?.parent?.model.concentrationtypecode.value == '0');

                    }
                  },
                  asyncValidators: {
                    inOptions: this.inOptions,
                  },
                },
                {
                  key: 'name',
                },
                {
                  key: 'resolutionmode',
                },
              ],
            },
            {
              className: 'flex-1',
              key: 'lowamountnumerunit',
              fieldGroup: [
                {
                  type: 'selectType',
                  key: 'value',
                  props: {
                    options: this.injector
                      .get(LookupGetService)
                      .loadCachedOptions('amountnumerunit'),
                    label: 'Numerator Unit',
                    required: true,

                    valueProp: 'code',
                    labelProp: 'name',
                    attributes: {
                      title:
                        'The Low Limit Numerator Unit or for non range measurements the Numerator Unit (PP.ACT.5/PP.EXC.5/PP.ADJ.5)',
                    },
                  },
                  expressionProperties: {
                    //This is needed for datamasking
                    'props.options': (model, formState, field) => {
                      if (field!.parent?.parent?.parent?.parent?.model.concentrationtypecode &&
                        field!.parent?.parent?.parent?.parent?.model.concentrationtypecode.value == '0') {
                        return [{ 'code': "0", 'name': '***' }]
                      }
                      return field!.props!['options'];
                    },
                    'props.disabled': (model, formState, field) => {
                      return (field!.parent?.parent?.parent?.parent?.model.concentrationtypecode &&
                        field!.parent?.parent?.parent?.parent?.model.concentrationtypecode.value == '0');

                    }
                  },
                  asyncValidators: {
                    inOptions: this.inOptions,
                  },
                },
                {
                  key: 'name',
                },
                {
                  key: 'resolutionmode',
                },
              ],
            },
          ],
        },
        {
          fieldGroupClassName: 'display-flex',
          fieldGroup: [
            {
              className: 'flex-1',
              type: 'masked-input',
              key: 'lowamountdenomvalue',
              props: {
                label: 'Denominator Value',
                required: true,
                disabled: false,
                maskChar: '*',
                maskIf: (field: any) => { return field.parent?.parent?.parent?.model.concentrationtypecode.value == '0' },
                attributes: {
                  title:
                    'The Low Limit Denominator Value or for non range measurements the Denominator Value (PP.ACT.6/PP.EXC.6/PP.ADJ.6)',
                },
              },
              expressionProperties: {
                //This is needed for datamasking
                'props.disabled': (model, formState, field) => {
                  return (field!.parent?.parent?.parent?.model.concentrationtypecode &&
                    field!.parent?.parent?.parent?.model.concentrationtypecode.value == '0');
                }
              },
              validators: {
                customValidator: {
                  expression: (c: any) => /^-?\d+(\.\d+)?$/.test(String(c.value).trim()),
                  message: () => 'Only numbers are allowed',
                },
              },
            },
            {
              className: 'flex-1',
              key: 'lowamountdenomprefix',
              fieldGroup: [
                {
                  type: 'selectType',
                  key: 'value',
                  props: {
                    options: this.injector
                      .get(LookupGetService)
                      .loadCachedOptions('amountprefix'),
                    label: 'Denominator Prefix',
                    required: true,
                    valueProp: 'code',
                    labelProp: 'name',
                    attributes: {
                      title:
                        'The Low Limit Denominator Prefix or for non range measurements the Denominator Prefix (PP.ACT.7/PP.EXC.7/PP.ADJ.7)',
                    },
                  },
                  expressionProperties: {
                    //This is needed for datamasking
                    'props.options': (model, formState, field) => {
                      if (field!.parent?.parent?.parent?.parent?.model.concentrationtypecode &&
                        field!.parent?.parent?.parent?.parent?.model.concentrationtypecode.value == '0') {
                        return [{ 'code': "0", 'name': '***' }]
                      } else {
                        return field!.props!['options'];
                      }
                    },
                    'props.disabled': (model, formState, field) => {
                      return (field!.parent?.parent?.parent?.parent?.model.concentrationtypecode &&
                        field!.parent?.parent?.parent?.parent?.model.concentrationtypecode.value == '0');

                    }
                  },
                  asyncValidators: {
                    inOptions: this.inOptions,
                  },
                },
                {
                  key: 'name',
                },
                {
                  key: 'resolutionmode',
                },
              ],
            },
            {
              className: 'flex-1',
              key: 'lowamountdenomunit',
              fieldGroup: [
                {
                  type: 'selectType',
                  key: 'value',
                  props: {
                    options: [],
                    label: 'Denominator Unit',
                    required: true,
                    valueProp: 'code',
                    labelProp: 'name',
                    attributes: {
                      title:
                        'The Low Limit Denominator Unit or for non range measurements the Denominator Unit (PP.ACT.8/PP.EXC.8/PP.ADJ.8)',
                    },
                  },
                  expressionProperties: {
                    //This is needed for datamasking
                    'props.options': (model, formState, field) => {
                      if (field!.parent?.parent?.parent?.parent?.model.concentrationtypecode &&
                        field!.parent?.parent?.parent?.parent?.model.concentrationtypecode.value == '0') {
                        return [{ 'code': "0", 'name': '***' }]
                      }
                      return field!.props!['options'];
                    },
                    'props.disabled': (model, formState, field) => {
                      return (field!.parent?.parent?.parent?.parent?.model.concentrationtypecode &&
                        field!.parent?.parent?.parent?.parent?.model.concentrationtypecode.value == '0');

                    }
                  },
                  asyncValidators: {
                    inOptions: this.inOptions,
                  },
                  hooks: {
                    onInit: (field) => this.expressedAsHook(field),

                    onDestroy: (field) => {
                      (field as any)._subscription?.unsubscribe();
                    },
                  },
                  validators: {
                    customValidator: {
                      expression: (
                        control: AbstractControl,
                        field: FormlyFieldConfig
                      ) => {
                        if (!(field as any)._subscriptionInit) {
                          this.expressedAsHook(field);
                        }

                        if (!(field as any)._firstExecutionDone) {
                          (field as any)._firstExecutionDone = true;
                          const realValue = control.value;
                          control.setValue(null);
                          control.setValue(realValue);
                        }

                        if (!control.value && field!.props!.required) {
                          return false;
                        }
                        return true;
                      },
                    },
                  },
                },
                {
                  key: 'name',
                },
                {
                  key: 'resolutionmode',
                },
              ],
            },
          ],
        },
      ],
    },
    {
      key: 'highAmountConditionalGroup',
      hideExpression: (model, formState, field) => {
        return field?.parent?.model.concentrationtypecode?.value != '2' || false;
      },
      fieldGroup: [
        {
          fieldGroupClassName: 'display-flex',
          fieldGroup: [
            {
              key: 'secondSectionLabel',
              className: 'flex-1',
              template: '<div>space reserved</div>',
            },
          ],
        },
        {
          fieldGroupClassName: 'display-flex',
          fieldGroup: [
            {
              className: 'flex-1',
              type: 'input',
              key: 'highamountnumervalue',
              props: {
                label: 'Numerator Value',
                required: true,
                attributes: {
                  title:
                    'The High Limit Numerator Value - for Range values only (PP.ACT.9/PP.EXC.9/PP.ADJ.9)',
                },
              },
              validators: {
                customValidator: {
                  expression: (c: any) => /^\d+(\.\d+)?$/.test(c.value),
                  message: () => 'Only numbers are allowed',
                },
              },
            },
            {
              className: 'flex-1',
              key: 'highamountnumerprefix',
              fieldGroup: [
                {
                  type: 'selectType',
                  key: 'value',
                  props: {
                    options: this.injector
                      .get(LookupGetService)
                      .loadCachedOptions('amountprefix'),
                    label: 'Numerator Prefix',
                    required: true,

                    valueProp: 'code',
                    labelProp: 'name',
                    attributes: {
                      title:
                        'The High Limit Numerator Prefix - for Range values only (PP.ACT.10/PP.EXC.10/PP.ADJ.10)',
                    },
                  },
                  asyncValidators: {
                    inOptions: this.inOptions,
                  },
                },
                {
                  key: 'name',
                },
                {
                  key: 'resolutionmode',
                },
              ],
            },
            {
              className: 'flex-1',
              key: 'highamountnumerunit',
              fieldGroup: [
                {
                  type: 'selectType',
                  key: 'value',
                  props: {
                    options: this.injector
                      .get(LookupGetService)
                      .loadCachedOptions('amountnumerunit'),
                    label: 'Numerator Unit',
                    valueProp: 'code',
                    labelProp: 'name',
                    required: true,
                    readonly: true,
                    disabled: true,
                    attributes: {
                      title:
                        'The High Limit Numerator Unit - for Range values only (PP.ACT.11/PP.EXC.11/PP.ADJ.11)',
                    },
                  },
                  asyncValidators: {
                    inOptions: this.inOptions,
                  },
                  hooks: {
                    onInit: (field) => {
                      const lowamountnumerunit =
                        field.parent!.parent!.parent!.parent!.fieldGroup![4]
                          .fieldGroup![1].fieldGroup![2].fieldGroup![0]
                          .formControl;
                      lowamountnumerunit?.valueChanges.subscribe((value) => {
                        field.formControl?.setValue(value);
                      });

                      lowamountnumerunit?.setValue(lowamountnumerunit?.value);
                    },
                  },
                },
                {
                  key: 'name',
                },
                {
                  key: 'resolutionmode',
                },
              ],
            },
          ],
        },
        {
          fieldGroupClassName: 'display-flex',
          fieldGroup: [
            {
              className: 'flex-1',
              type: 'input',
              key: 'highamountdenomvalue',
              props: {
                label: 'Denominator Value',
                readonly: true,
                required: true,
                disabled: true,
                attributes: {
                  title:
                    'The High Limit Denominator Value - for Range measurements the Denominator Value (PP.ACT.12/PP.EXC.12/PP.ADJ.12)',
                },
              },
              validators: {
                customValidator: {
                  expression: (c: any) => /^\d+(\.\d+)?$/.test(c.value),
                  message: () => 'Only numbers are allowed',
                },
              },
              hooks: {
                onInit: (field) => {
                  const lowamountdenomvalueFormControl =
                    field.parent!.parent!.parent!.fieldGroup![4].fieldGroup![2]
                      .fieldGroup![0].formControl;
                  lowamountdenomvalueFormControl?.valueChanges.subscribe(
                    (value) => {
                      field.formControl?.setValue(value);
                    }
                  );

                  lowamountdenomvalueFormControl?.setValue(
                    lowamountdenomvalueFormControl.value
                  );
                },
              },
            },
            {
              className: 'flex-1',
              key: 'highamountdenomprefix',
              fieldGroup: [
                {
                  type: 'selectType',
                  key: 'value',
                  props: {
                    options: this.injector
                      .get(LookupGetService)
                      .loadCachedOptions('amountprefix'),
                    label: 'Denominator Prefix',
                    valueProp: 'code',
                    labelProp: 'name',
                    readonly: true,
                    required: true,
                    disabled: true,
                    attributes: {
                      title:
                        'The High Limit Denominator Prefix - for Range values only (PP.ACT.13/PP.EXC.13/PP.ADJ.13)',
                    },
                  },
                  asyncValidators: {
                    inOptions: this.inOptions,
                  },
                  hooks: {
                    onInit: (field) => {
                      const lowamountdenomprefixFormControl =
                        field.parent!.parent!.parent!.parent!.fieldGroup![4]
                          .fieldGroup![2].fieldGroup![1].fieldGroup![0]
                          .formControl;
                      lowamountdenomprefixFormControl?.valueChanges.subscribe(
                        (value) => {
                          field.formControl?.setValue(value);
                        }
                      );

                      lowamountdenomprefixFormControl?.setValue(
                        lowamountdenomprefixFormControl?.value
                      );
                    },
                  },
                },
                {
                  key: 'name',
                },
                {
                  key: 'resolutionmode',
                },
              ],
            },
            {
              className: 'flex-1',
              key: 'highamountdenomunit',
              fieldGroup: [
                {
                  type: 'selectType',
                  key: 'value',
                  props: {
                    options: [],
                    label: 'Denominator Unit',
                    valueProp: 'code',
                    labelProp: 'name',
                    readonly: true,
                    required: true,
                    disabled: true,
                    attributes: {
                      title:
                        'The High Limit Denominator Unit - for Range values only (PP.ACT.14/PP.EXC.14/PP.ADJ.14)',
                    },
                  },
                  asyncValidators: {
                    inOptions: this.inOptions,
                  },
                },
                {
                  key: 'name',
                },
                {
                  key: 'resolutionmode',
                },
              ],
            },
          ],
        },
      ],
    },
  ];

  drugIngredientFields: FormlyFieldConfig = {
    fieldGroup: [...this.drugIngredientItemFields],
  };

  productIndicationItemFields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          type: 'selectType',
          key: 'meddraversion',
          props: {
            label: 'MedDRA version',
            required: true,
            options: this.getMeddraVersions(),
            valueProp: 'code',
            labelProp: 'name',
            attributes: {
              title:
                'During an insert or an update of an AMP, each indication should be coded using MedDRA in its latest current version. The next official MedDRA version can also be referenced. (AP.IND.1/DP.IND.1)',
            },
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },

    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          type: 'selectType',
          key: 'meddralevel',
          props: {
            label: 'MedDRA level',
            required: true,
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('meddraLevel')
              .pipe(
                map((options: CodeName[]) =>
                  options.filter((option: CodeName) =>
                    this.isPtOrLltMeddraLevel(String(option.code).toUpperCase())
                  )
                )
              ),
            valueProp: 'code',
            labelProp: 'name',
            attributes: {
              title:
                'Preferred Term (PT) or Low Level Term (LLT) must be selected. (AP.IND.2/DP.IND.2)',
            },
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
          validators: {
            meddraLevelPTLLTValidator: this.meddraLevelPTLLTValidator,
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },

    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-4 ml-5',
          key: 'meddracode',
          fieldGroup: [
            {
              type: 'input',
              key: 'value',
              wrappers: ['modal-wrapper'],
              props: {
                lookup: {},
                label: 'MedDRA term',
                readonly: true,
                required: true,

                attributes: {
                  title:
                    'MedDRA terms corresponding to the description of the authorised indication(s), signs, symptoms or intended effects as reflected in Section 4.1 Therapeutic Indications of the SmPC must be specified. (AP.IND.3/DP.IND.3)',
                },
              },
              validators: {
                lookupValidator: this.lookupMeddraValidator,
              },
              hooks: {
                onInit: (field) => {
                  const meddraLevelControl =
                    field.parent!.parent!.parent!.get!(
                      'meddralevel'
                    ).formControl;

                  const meddraVersionControl =
                    field.parent!.parent!.parent!.get!(
                      'meddraversion'
                    ).formControl;

                  const meddraValueControl = field.formControl;

                  const meddraNameControl =
                    field.parent!.get!('name').formControl;

                  if (
                    meddraLevelControl?.getRawValue() == undefined ||
                    meddraVersionControl?.getRawValue() == undefined
                  ) {
                    meddraValueControl?.disable();
                  } else {
                    field.props!['lookup'] = {
                      name: meddraLevelControl.getRawValue(),
                      description: 'Select MedDRA term',
                      type: 'R',
                      columns: [
                        new ListColumn('Num', ListDataColumn.TEXT, 'num'),
                        new ListColumn(
                          'MedDRA Code',
                          ListDataColumn.TEXT,
                          'code'
                        ),
                        new ListColumn('Name', ListDataColumn.TEXT, 'name'),
                      ],
                      parameters: [
                        ['meddraversion', meddraVersionControl.getRawValue()],
                      ],
                    };
                  }

                  meddraLevelControl!.valueChanges
                    .pipe(scan((acc, next) => { return { prev: acc.next, next } },
                      { prev: null, next: meddraLevelControl!.value }))
                    .subscribe(({ prev, next }) => {
                      if (prev != next) {
                        if (
                          meddraLevelControl?.getRawValue() &&
                          meddraVersionControl?.getRawValue()
                        ) {
                          meddraValueControl?.enable();
                        }

                        let meddraversion = meddraVersionControl!.getRawValue();
                        field.props!['lookup'] = {
                          name: next,
                          description: 'Select MedDRA term',
                          type: 'R',
                          columns: [
                            new ListColumn('Num', ListDataColumn.TEXT, 'num'),
                            new ListColumn(
                              'MedDRA Code',
                              ListDataColumn.TEXT,
                              'code'
                            ),
                            new ListColumn('Name', ListDataColumn.TEXT, 'name'),
                          ],
                          parameters: [['meddraversion', meddraversion]],
                        };
                        delete field.parent!.parent!.model['meddracode'];
                      }
                    });

                  meddraVersionControl!.valueChanges
                    .pipe(scan((acc, next) => { return { prev: acc.next, next } },
                      { prev: null, next: meddraVersionControl!.value }))
                    .subscribe(({ prev, next }) => {
                      if (prev != next) {
                        if (
                          meddraLevelControl?.getRawValue() &&
                          meddraVersionControl?.getRawValue()
                        ) {
                          meddraValueControl?.enable();
                        }

                        let meddralevel = meddraLevelControl!.getRawValue();
                        field.props!['lookup'] = {
                          name: meddralevel,
                          description: 'Select MedDRA term',
                          type: 'R',
                          columns: [
                            new ListColumn('Num', ListDataColumn.TEXT, 'num'),
                            new ListColumn(
                              'MedDRA Code',
                              ListDataColumn.TEXT,
                              'code'
                            ),
                            new ListColumn('Name', ListDataColumn.TEXT, 'name'),
                          ],
                          parameters: [['meddraversion', next]],
                        };
                        if (meddraValueControl?.value) {
                          this.injector!.get(LookupGetService).getLookup(meddralevel, meddraValueControl?.value + "_v" + next)
                            .then((result: CodeName) => {
                              if (result && result.name && result.name != '') {
                                meddraNameControl?.setValue(result.name);
                                meddraValueControl?.setValue(result.code);
                              } else {
                                this.injector.get(MessageService).add({ severity: 'warn', detail: 'The MedDRA term was not found in the selected version. Please selected another term.', life: environment.warningElapsedTime });
                                delete field.parent!.parent!.model['meddracode'];
                              }
                            });
                        } else {
                          delete field.parent!.parent!.model['meddracode'];
                        }
                      }
                    });
                },
              },
            },
            {
              key: 'name',
            },
            {
              key: 'resolutionmode',
            },
          ],
        },
      ],
    },
  ];

  previousEvCodeFields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'type',
          defaultValue: undefined,
          type: 'selectType',
          props: {
            label: 'Type',
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('productstypes'),
            valueProp: 'code',
            labelProp: 'name',
            required: false,
            readonly: true,
            disabled: false,
            attributes: {
              title: 'Type',
            },
            asyncValidators: {
              inOptions: this.inOptions,
            },
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'devevcode',
          fieldGroup: [
            {
              type: 'input',
              key: 'value',
              wrappers: ['modal-wrapper'],
              props: {
                label: 'Previous EV Code',
                required: true,

                readonly: true,
                attributes: {
                  title:
                    'The EV Code of a development product may be specified if the authorised product was submitted to the XEVMPD in its development form. The EV Code must not be a nullified EV Code. The EV Code of an authorised product must be specified in the context of a transfer/renewal of the marketing authorisation as per guidance described for this field in Chapter 3.II. The EV Code must not be a nullified EV Code.  (AP.PEV.1/DP.PEV.1)',
                },
              },
              expressions: {
                'props.disabled': (field: FormlyFieldConfig) => {
                  return !field.parent!.parent!.parent!.get!(
                    'type'
                  ).formControl!.getRawValue();
                },
              },
              hooks: {
                onInit: (field) => {
                  //Get type control
                  const type =
                    field.parent!.parent!.parent!.get!('type').formControl!;

                  if (
                    field.parent!.parent!.parent!.get!(
                      'type'
                    ).formControl!.getRawValue()
                  ) {
                    field.props!['lookup'] = {
                      name: 'previousevcode',
                      description: 'Select Previous EV Code',
                      type: 'R',
                      columns: [
                        new ListColumn('Num', ListDataColumn.TEXT, 'num'),
                        new ListColumn('EV Code', ListDataColumn.TEXT, 'code'),
                        new ListColumn('Name', ListDataColumn.TEXT, 'name'),
                      ],
                      parameters: [['type', type.getRawValue()]],
                    };
                  }

                  field.parent!.parent!.parent!.get!(
                    'type'
                  ).formControl!.valueChanges.subscribe((newValue: any) => {
                    field.props!['lookup'] = {
                      name: 'previousevcode',
                      description: 'Select Previous EV Code',
                      type: 'R',
                      columns: [
                        new ListColumn('Num', ListDataColumn.TEXT, 'num'),
                        new ListColumn('EV Code', ListDataColumn.TEXT, 'code'),
                        new ListColumn('Name', ListDataColumn.TEXT, 'name'),
                      ],
                      parameters: [['type', newValue]],
                    };
                  });
                },
              },
            },
            {
              key: 'name',
            },
            {
              key: 'resolutionmode',
            },
          ],
        },
      ],
    },
  ];

  pharmaceuticalFormCodeItem: FormlyFieldConfig[] = [
    {
      key: 'pharmformcode',
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          key: 'value',
          type: 'input',
          className: 'flex-1',
          wrappers: ['modal-wrapper'],
          props: {
            lookup: {
              name: 'pharmform',
              description: 'Select Pharmaceutical Form',
              type: 'LR',
              columns: [
                new ListColumn('Num', ListDataColumn.TEXT, 'num'),
                new ListColumn('EV Code', ListDataColumn.TEXT, 'code'),
                new ListColumn('Name', ListDataColumn.TEXT, 'name'),
                new ListColumn('Type', ListDataColumn.TEXT, 'type'),
              ],
            },
            label: 'Administrable Pharmaceutical Form',
            required: true,

            readonly: true,
            attributes: {
              title:
                'The name(s) of the pharmaceutical form(s) for administration to the patient, after any necessary transformation of the authorised pharmaceutical form(s) has been carried out, must be specified in this field as per guidance described for this field in Chapter 3.II. Standard term(s) of the administrable pharmaceutical form(s) must be referenced whenever possible. (PP.1)',
            },
          },
          validators: {
            lookupValidator: this.lookupValidator,
          },
          expressions: {
            'props.lookup.parameters': (field: FormlyFieldConfig) => {
              if (
                this.injector.get(Art57Section).getSelectedSection()
                  .selectedNode.parent?.parent?.data.model.type == 2
              ) {
                //Authorised
                return [['type', '2|3']];
              } else {
                //Development
                //No filters needed
                return null;
              }
            },
          },
        },
        {
          key: 'resolutionmode',
        },
        {
          key: 'name',
        },
      ],
    },
  ];

  pharmaceuticalProductMedicalDeviceItemFields: FormlyFieldConfig[] = [
    {
      key: 'medicaldevicecode',
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          type: 'selectType',
          key: 'value',
          props: {
            label: 'Medical Device',
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('medicaldevice'),
            valueProp: 'code',
            labelProp: 'name',
            required: true,
            attributes: {
              title:
                'If applicable, the EV code of a medical device where it forms an integral part of the advanced therapy medicinal product must be specified if applicable. (PP.MD.1)',
            },
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
          hooks: {
            onInit: (field) => {
              field.formControl!.valueChanges.subscribe(value => {
                const selected = this.injector
                  .get(LookupGetService)
                  .retrieveValueFromCache('medicaldevice', value);

                field.form!.get('name')?.setValue(selected || '');
                if (field.parent?.model) {
                  field.parent.model.name = selected || '';
                }
              });
            }
          }
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
  ];

  pharmaceuticalProductDrugRoutesItemFields: FormlyFieldConfig[] = [
    {
      key: 'adminroutecode',
      fieldGroup: [
        {
          fieldGroupClassName: 'display-flex',
          fieldGroup: [
            {
              className: 'flex-1',
              key: 'value',
              type: 'input',
              wrappers: ['modal-wrapper'],
              props: {
                lookup: {
                  name: 'adminroute',
                  description: 'Select Administration Route',
                  type: 'LR',
                  columns: [
                    new ListColumn('Num', ListDataColumn.TEXT, 'num'),
                    new ListColumn('EV Code', ListDataColumn.TEXT, 'code'),
                    new ListColumn('Name', ListDataColumn.TEXT, 'name'),
                    new ListColumn('Type', ListDataColumn.TEXT, 'type'),
                  ],
                },
                label: 'Route Of Administration',
                required: true,

                readonly: true,
                localCode: 'administrationroutes',
                showLocalButton: true,
                attributes: {
                  title:
                    'The route(s) of administration through which the administrable pharmaceutical product(s)  is taken into/makes contact with the body, as indicated in Section 4.2. Posology and method of administration of the SmPC, must must be specified in this field as per guidance described for this field in Chapter 3.II. Standard term(s) of the route(s) of administration must be referenced whenever possible. (PP.AR.1)',
                },
              },
              validators: {
                lookupValidator: this.lookupValidator,
              },
              expressions: {
                'props.lookup.parameters': (field: FormlyFieldConfig) => {
                  if (this.injector
                    .get(Art57Section)
                    .getSelectedSection().selectedNode.parent?.parent?.parent?.parent?.data.model.type == 2) {
                    //Authorised
                    return [['type', '2|3']];
                  } else {
                    //Development
                    //No filters needed
                    return null;
                  }

                },
              },
            },
            {
              key: 'resolutionmode',
            },
            {
              key: 'name',
            },
          ],
        },
      ],
    },
  ];

  developmentSubstanceFields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'ev_Code',
          defaultValue: '',
          type: 'input',
          props: {
            label: 'EV Code',
            required: true,
            readonly: true,
            disabled: true,
            attributes: {
              title: 'EV Code',
            },
          },
          hideExpression: (model, formState, field) => {
            return field!.parent!.model.operationtype === 1;
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'type',
          defaultValue: 1,
          type: 'selectType',
          props: {
            label: 'Type',
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('substancestypes'),
            valueProp: 'code',
            labelProp: 'name',
            required: true,
            readonly: true,
            disabled: true,
            attributes: {
              title: 'Type',
            },
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'operationtype',
          defaultValue: 1,
          type: 'select',
          props: {
            label: 'Operation Type',
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('operationtype'),
            valueProp: 'code',
            labelProp: 'name',
            required: true,
            readonly: true,
            disabled: true,
            attributes: {
              title: 'Operation Type',
            },
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      key: 'localnumber',
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          key: 'newownerid',
          className: 'flex-1',
          type: 'input',
          wrappers: ['modal-wrapper'],
          hideExpression: () => {
            return !this.injector.get(AuthService).isEMAOrganisation();
          },
          props: {
            label: 'Owner ID',
            required: false,

            placeholder: '',
            lookup: {
              name: 'senderid',
              description: 'Select Owner',
              type: 'R',
              remoteMode: 'cache',
              columns: [
                new ListColumn('Num', ListDataColumn.TEXT, 'num'),
                new ListColumn('EV ID', ListDataColumn.TEXT, 'code'),
                new ListColumn('Name', ListDataColumn.TEXT, 'name'),
                new ListColumn('Country', ListDataColumn.TEXT, 'countryname'),
                new ListColumn('Connection', ListDataColumn.TEXT, 'trading'),
                new ListColumn(
                  'Type',
                  ListDataColumn.TEXT,
                  'organisationevtype'
                ),
                new ListColumn(
                  'EV Profile Type',
                  ListDataColumn.TEXT,
                  'evprofile'
                ),
              ],
            },
            readonly: true,
            attributes: {
              title:
                'Select the Owner for the Development Substance (Insert operation type only) (DS.3)',
            },
          },
          validators: {
            lookupValidator: this.lookupSenderIdValidator,
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'substancename',
          type: 'input',
          props: {
            label: 'Current Name or Code',
            placeholder: '',
            required: true,
            attributes: {
              title: '',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'iscode',
          type: 'selectType',
          props: {
            defaultValue: null,
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('substancenameorcode'),
            valueProp: 'code',
            labelProp: 'name',
            label: 'Code or Name',
            required: true,
            attributes: {
              title: '',
            },
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      key: 'preferred',
      defaultValue: 1,
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'substanceclass',
          type: 'selectType',
          props: {
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('substanceclass'),
            label: 'Class',
            required: false,

            valueProp: 'code',
            labelProp: 'name',
            attributes: {
              title:
                'You may Classify the Substance using a value from the Lookup List (DS.7)',
            },
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'sponsorcode',
          fieldGroup: [
            {
              key: 'value',
              type: 'input',
              wrappers: ['modal-wrapper'],
              props: {
                label: 'Sponsor',
                placeholder: '',
                required: true,

                lookup: {
                  name: 'organisation',
                  description: 'Select Sponsor',
                  parameters: [['type', 2]],
                  columns: [
                    new ListColumn('Num', ListDataColumn.TEXT, 'num'),
                    new ListColumn('EV Code', ListDataColumn.TEXT, 'code'),
                    new ListColumn('Sponsor Name', ListDataColumn.TEXT, 'name'),
                    new ListColumn(
                      'Validated',
                      ListDataColumn.DATE,
                      'validated'
                    ),
                  ],
                  type: 'LR',
                },
                readonly: true,
                attributes: {
                  title: 'Sponsor (DS.4)',
                },
              },
              validators: {
                lookupValidator: this.lookupValidator,
              },
            },
            {
              key: 'name',
            },
            {
              key: 'resolutionmode',
            },
          ],
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'casnumber',
          type: 'input',
          props: {
            label: 'CAS Number',
            placeholder: '',
            required: false,

            attributes: {
              title: 'CAS Number (DS.5)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'molecularformula',
          type: 'textarea',
          props: {
            label: 'Molecular Formula',
            rows: 5,
            placeholder: '',
            required: false,

            attributes: {
              title: 'Molecular Formula (DS.6)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'cbd',
          type: 'textarea',
          props: {
            label: 'Chemical/Biological Description',
            rows: 5,
            placeholder: '',
            required: false,

            attributes: {
              title: 'Chemical / Biological Description (DS.8)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'comments',
          type: 'textarea',
          props: {
            label: 'Comment',
            rows: 5,
            placeholder: '',
            required: false,

            attributes: {
              title: 'Comment (DS.9)',
            },
            maxLength: 500
          },
          expressions: {
            'props.required': (field: FormlyFieldConfig) => {
              return this.isNullifyOperation(this.getFieldOperationType(field));
            },
          },
        },
      ],
    },
  ];

  approvedSubstanceFields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'ev_Code',
          defaultValue: '',
          type: 'input',
          props: {
            label: 'EV Code',
            required: true,
            readonly: true,
            disabled: true,
            attributes: {
              title: 'EV Code',
            },
          },
          hideExpression: (model, formState, field) => {
            return field!.parent!.model.operationtype === 1;
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'type',
          defaultValue: 2,
          type: 'selectType',
          props: {
            label: 'Type',
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('substancestypes'),
            valueProp: 'code',
            labelProp: 'name',
            required: true,
            readonly: true,
            disabled: true,
            attributes: {
              title: 'Type',
            },
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'operationtype',
          defaultValue: 1,
          type: 'select',
          props: {
            label: 'Operation Type',
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('operationtype'),
            valueProp: 'code',
            labelProp: 'name',
            required: true,
            readonly: true,
            disabled: true,
            attributes: {
              title: 'Operation Type',
            },
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      key: 'localnumber',
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'substancename',
          type: 'input',
          props: {
            label: 'Substance Name',
            placeholder: '',
            required: true,

            attributes: {
              title: 'Name of the substance (AS.4)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'substanceclass',
          type: 'selectType',
          props: {
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('substanceclass'),
            label: 'Class',
            required: false,

            valueProp: 'code',
            labelProp: 'name',
            attributes: {
              title:
                'You may Classify the Substance using a value from the Lookup List (AS.7)',
            },
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'sourcecode',
          fieldGroup: [
            {
              key: 'value',
              type: 'input',
              wrappers: ['modal-wrapper'],
              props: {
                label: 'Source',
                placeholder: '',
                required: true,

                lookup: {
                  name: 'source',
                  description: 'Select Source',
                  type: 'LR',
                  columns: [
                    new ListColumn('Num', ListDataColumn.TEXT, 'num'),
                    new ListColumn('EV Code', ListDataColumn.TEXT, 'code'),
                    new ListColumn('Name', ListDataColumn.TEXT, 'name'),
                  ],
                },
                readonly: true,
                attributes: {
                  title: 'Source (AS.3)',
                },
              },
              validators: {
                lookupValidator: this.lookupValidator,
              },
            },
            {
              key: 'name',
            },
            {
              key: 'resolutionmode',
            },
          ],
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'casnumber',
          type: 'input',
          props: {
            label: 'CAS Number',
            placeholder: '',
            required: false,

            attributes: {
              title: 'CAS Number (AS.5)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'molecularformula',
          type: 'textarea',
          props: {
            label: 'Molecular Formula',
            rows: 5,
            placeholder: '',
            required: false,

            attributes: {
              title: 'Molecular Formula (AS.6)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'cbd',
          type: 'textarea',
          props: {
            label: 'Chemical/Biological Description',
            placeholder: '',
            rows: 5,
            required: false,

            attributes: {
              title: 'Chemical / Biological Description (AS.8)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'comments',
          type: 'textarea',
          props: {
            label: 'Comment',
            rows: 5,
            placeholder: '',
            required: false,

            attributes: {
              title: 'Comment (AS.10)',
            },
            maxLength: 500
          },
          expressions: {
            'props.required': (field: FormlyFieldConfig) => {
              return this.isNullifyOperation(this.getFieldOperationType(field));
            },
          },
        },
      ],
    },
  ];

  substanceTranslationFields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'languagecode',
          type: 'selectType',
          props: {
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('language'),
            label: 'Language Code',
            required: true,

            valueProp: 'code',
            labelProp: 'name',
            attributes: {
              title: 'Language Code (AS.T.1)',
            },
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'term',
          type: 'input',
          props: {
            label: 'Translation',
            placeholder: '',
            required: true,

            attributes: {
              title: 'Translation of the Substance (AS.T.2)',
            },
          },
        },
      ],
    },
  ];

  substanceAliasFields: FormlyFieldConfig[] = [
    {
      key: 'sourcecode',
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'value',
          type: 'input',
          wrappers: ['modal-wrapper'],
          props: {
            label: 'Source',
            required: true,

            lookup: {
              name: 'source',
              description: 'Select Source',
              type: 'LR',
              columns: [
                new ListColumn('Num', ListDataColumn.TEXT, 'num'),
                new ListColumn('EV Code', ListDataColumn.TEXT, 'code'),
                new ListColumn('Name', ListDataColumn.TEXT, 'name'),
              ],
            },
            readonly: true,
            attributes: {
              title: 'Source (AS.SA.1)',
            },
          },
          validators: {
            lookupValidator: this.lookupValidator,
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'aliasname',
          type: 'input',
          props: {
            label: 'Alias Name',
            placeholder: '',
            required: true,

            attributes: {
              title: 'A different name describing a substance (AS.SA.2)',
            },
          },
        },
      ],
    },
  ];

  substanceAliasTranslationsFields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'languagecode',
          type: 'selectType',
          props: {
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('language'),
            label: 'Language',
            required: true,

            valueProp: 'code',
            labelProp: 'name',
            attributes: {
              title: 'Language (SA.T.1)',
            },
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'term',
          type: 'input',
          props: {
            label: 'Translation',
            placeholder: '',
            required: true,

            attributes: {
              title: 'Translation of the Alias Term (SA.T.2)',
            },
          },
        },
      ],
    },
  ];

  substanceInternationalCodeFields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'sourcecode',
          fieldGroup: [
            {
              key: 'value',
              type: 'input',
              wrappers: ['modal-wrapper'],
              props: {
                label: 'Source',
                placeholder: '',
                required: true,

                lookup: {
                  name: 'source',
                  description: 'Select Source',
                  type: 'LR',
                  columns: [
                    new ListColumn('Num', ListDataColumn.TEXT, 'num'),
                    new ListColumn('EV Code', ListDataColumn.TEXT, 'code'),
                    new ListColumn('Name', ListDataColumn.TEXT, 'name'),
                  ],
                },
                readonly: true,
                attributes: {
                  title: 'Source (AS.IC.1)',
                },
              },
              validators: {
                lookupValidator: this.lookupValidator,
              },
            },
            {
              key: 'resolutionmode',
            },
            {
              key: 'name',
            },
          ],
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'referencetext',
          fieldGroup: [
            {
              key: 'value',
              type: 'input',
              props: {
                label: 'International Code',
                placeholder: '',
                required: true,

                attributes: {
                  title: 'International Code of the Substance (AS.IC.2)',
                },
              },
            },
          ],
        },
      ],
    },
  ];

  substanceParentCodeFields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'sourcecode',
          fieldGroup: [
            {
              key: 'value',
              type: 'selectType',
              props: {
                options: this.injector
                  .get(LookupGetService)
                  .loadCachedOptions('substancetype'),
                label: 'Substance Type',
                required: true,

                valueProp: 'code',
                labelProp: 'name',
              },
              asyncValidators: {
                inOptions: this.inOptions,
              },
            },
            {
              key: 'name',
            },
            {
              key: 'resolutionmode',
              defaultValue: 2,
            },
          ],
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'referencetext',
          fieldGroup: [
            {
              key: 'value',
              type: 'input',
              wrappers: ['modal-wrapper'],
              props: {
                label: 'Parent Substance',
                placeholder: '',
                required: true,

                lookup: {
                  name: 'substanceparentcode',
                  description: 'Select Substance',
                  type: 'LR',
                  columns: [
                    new ListColumn('Num', ListDataColumn.TEXT, 'num'),
                    new ListColumn('EV Code', ListDataColumn.TEXT, 'code'),
                    new ListColumn(
                      'Substance Name',
                      ListDataColumn.TEXT,
                      'name'
                    ),
                    new ListColumn(
                      'Match Type',
                      ListDataColumn.TEXT,
                      'matchType'
                    ),
                    new ListColumn(
                      'Class',
                      ListDataColumn.TEXT,
                      'classification'
                    ),
                  ],
                },
                readonly: true,
              },
              validators: {
                lookupValidator: this.lookupValidator,
              },
              expressions: {
                'props.lookup.parameters': (field: FormlyFieldConfig) => {
                  if (
                    this.injector.get(Art57Section).getSelectedSection().selectedNode.data.model.sourcecode.value
                  ) {
                    return [['type', this.injector.get(Art57Section).getSelectedSection().selectedNode.data.model.sourcecode.value]];
                  } else {
                    return null;
                  }
                }
              }
            },
            {
              key: 'name',
            },
            {
              key: 'resolutionmode',
            },
          ],
        },
      ],
    },
  ];

  substancePreviousEvCodeFields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'devevcode',
          fieldGroup: [
            {
              type: 'input',
              key: 'value',
              props: {
                label: 'Previous EV Code',
                required: true,
              },
            },
          ],
        },
      ],
    },
  ];

  substancePreviousNameCodeFields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'substancename',
          type: 'input',
          props: {
            label: 'Substance Name or Code',
            placeholder: '',
            required: true,
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'iscode',
          type: 'selectType',
          props: {
            defaultValue: null,
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('substancenameorcode'),
            valueProp: 'code',
            labelProp: 'name',
            label: 'Is Name or Code',
            required: false,
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      key: 'preferred',
      defaultValue: 2,
    },
  ];

  psiattachmentFields: FormlyFieldConfig[] = [
    {
      key: 'attachmentcode',
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          type: 'input',
          key: 'value',
          wrappers: ['modal-wrapper'],
          props: {
            lookup: {
              name: 'psiattachment',
              description: 'Select PSI',
              columns: [
                new ListColumn('Num', ListDataColumn.TEXT, 'num'),
                new ListColumn('EV Code', ListDataColumn.TEXT, 'code'),
                new ListColumn('Name', ListDataColumn.TEXT, 'name'),
                new ListColumn('Language', ListDataColumn.TEXT, 'language'),
                new ListColumn(
                  'Substance EV Code',
                  ListDataColumn.TEXT,
                  'productOrSubstance_ev_code'
                ),
                new ListColumn(
                  'Substance Name',
                  ListDataColumn.TEXT,
                  'productOrSubstanceName'
                ),
              ],
              type: 'LR',
            },
            label: 'Substance Attachment',
            required: true,
            readonly: true,
          },
          validators: {
            lookupValidator: this.lookupValidator,
          },
        },
        {
          key: 'resolutionmode',
          defaultValue: 1,
        },
        {
          key: 'name',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'validitydeclaration',
          type: 'selectType',
          props: {
            label: 'Validity declaration',
            required: false,
            defaultValue: null,
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('validitydeclaration'),
            valueProp: 'code',
            labelProp: 'name',
          },
          asyncValidators: {
            inOptions: this.inOptions,
          }
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
  ];

  ppiattachmentItemFields: FormlyFieldConfig[] = [
    {
      key: 'attachmentcode',
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          type: 'input',
          key: 'value',
          wrappers: ['modal-wrapper'],
          hooks: {
            onInit: (field) => {
              const control = field.formControl!;
              let originalValue = control.value;

              return control.valueChanges.pipe(
                distinctUntilChanged(),
                filter((value) => {
                  const hasChanged = value !== originalValue;
                  originalValue = value;
                  return hasChanged;
                }),
                tap(() => {
                  field.parent!.parent!
                    .get!('validitydeclaration')
                    .formControl!
                    .setValue(null);
                }),
              );
            },
          },
          props: {
            lookup: {
              name: 'ppiattachment',
              description: 'Select PPI',
              type: 'LR',
              columns: [
                new ListColumn('Num', ListDataColumn.TEXT, 'num'),
                new ListColumn('EV Code', ListDataColumn.TEXT, 'code'),
                new ListColumn('Name', ListDataColumn.TEXT, 'name'),
                new ListColumn('Language', ListDataColumn.TEXT, 'language'),
                new ListColumn(
                  'Product EV Code',
                  ListDataColumn.TEXT,
                  'productOrSubstance_ev_code'
                ),
                new ListColumn(
                  'Product Name',
                  ListDataColumn.TEXT,
                  'productOrSubstanceName'
                ),
              ],
            },
            label: 'Product Attachment',
            required: true,

            readonly: true,
            showLocalButton: true,
            localCode: 'attachments',
            attributes: {
              title:
                'The attachment referring to the authorised medicinal product must be specified. (AP.PPI.1/DP.PPI.1)',
            },
          },
          validators: {
            lookupValidator: this.lookupValidator,
          },
        },
        {
          key: 'resolutionmode',
          defaultValue: 1,
        },
        { key: 'name' },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'validitydeclaration',
          type: 'selectType',
          props: {
            label: 'Validity declaration',
            required: false,
            defaultValue: null,
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('validitydeclaration'),
            valueProp: 'code',
            labelProp: 'name',
            attributes: {
              title:
                'Validity confirmation that the referenced attachment is the latest version of the documentation must be provided when performing an update or insert of an authorised product where a referenced PPI attachment was previously loaded in the XEVMPD. (AP.PPI.2/DP.PPI.2)',
            },
          },
          expressions: {
            'props.required': (field) => {
              return [1,2].includes(field.props!["operationtype"]) &&
              field.parent!.parent!.get!("attachmentcode").get!("resolutionmode").formControl!.value===2; 
            },
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
          validators: {
            BR: this.AP_PPI_2_BR
          }
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
  ];

  attachmentFields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'ev_Code',
          defaultValue: '',
          type: 'input',
          props: {
            label: 'EV Code',
            required: true,
            readonly: true,
            disabled: true,
            attributes: {
              title: 'EV Code',
            },
          },
          hideExpression: (model, formState, field) => {
            return field!.parent!.model.operationtype === 1;
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'operationtype',
          defaultValue: 1,
          type: 'select',
          props: {
            label: 'Operation Type',
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('operationtype'),
            valueProp: 'code',
            labelProp: 'name',
            required: true,
            readonly: true,
            disabled: true,
            attributes: {
              title: 'Operation Type',
            },
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      key: 'localnumber',
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'attachmenttype',
          type: 'selectType',
          props: {
            label: 'Type (ATT.5)',
            defaultValue: null,
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('attachmenttype'),
            valueProp: 'code',
            labelProp: 'name',
            required: true,
            readonly: true,
            disabled: true,
            attributes: {
              title: 'Type (ATT.5)',
            },
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
          hooks: {
            onInit: (field) => {
              let previousValue: any = undefined;
              field.formControl?.valueChanges.subscribe((newValue: any) => {
                if (field.formControl?.value != previousValue) {
                  let filetype =
                    field.parent!.parent!.fieldGroup![4].fieldGroup![3]; // this refers to filetype field
                  const currentValue = filetype.formControl!.value;

                  if (field.formControl?.value == 1) {
                    filetype.props!['options'] = this.injector
                      .get(LookupGetService)
                      .loadCachedOptions('ppiattachmentfiletype');
                    filetype.formControl!.setValue(
                      currentValue ? currentValue : 1
                    );
                  } else if (field.formControl?.value == 2) {
                    filetype.props!['options'] = this.injector
                      .get(LookupGetService)
                      .loadCachedOptions('psiattachmentfiletype');
                    filetype.formControl!.setValue(
                      currentValue ? currentValue : 1
                    );
                  } else {
                    filetype.formControl!.setValue(
                      currentValue ? currentValue : null
                    );
                  }

                  previousValue = newValue;
                }
              });
              field.formControl!.setValue(field.formControl!.value);
            },
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'fileclass flex-1',
          key: 'file',
          type: 'file',
          props: {
            label: 'Attachment File',
            required: true,
          },
          validation: {
            messages: {
              required: 'Field is mandatory, please attach the file via the link under \'here\'.',
            }
          },
        },
        {
          className: 'flex-2',
          key: 'attachmentname',
          type: 'input',
          props: {
            label: 'Attachment Name',
            required: true,

            attributes: {
              title:
                'Enter a logical name allowing you to identify this attachment when associating',
            },
          },
        },
        {
          key: 'filename',
        },
        {
          className: 'flex-1',
          key: 'filetype',
          type: 'selectType',
          props: {
            label: 'File Type',
            options: undefined,
            valueProp: 'code',
            labelProp: 'name',
            required: true,

            attributes: {
              title: 'File Type (ATT.3)',
            },
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'languagecode',
          type: 'selectType',
          props: {
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('language'),
            label: 'Language',
            required: true,
            valueProp: 'code',
            labelProp: 'name',
            attributes: {
              title: 'Select the appropiate language of the attachment (ATT.6)',
            },
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'languagecode2',
          type: 'selectType',
          props: {
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('language'),
            label: 'Second Language',
            valueProp: 'code',
            labelProp: 'name',

            attributes: {
              title: 'Select the second language of the attachment (ATT.7)',
            },
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'attachmentversion',
          type: 'input',
          props: {
            label: 'Version Number',
            required: true,

            attributes: {
              title:
                'Enter up to 2 characters to identify your version of this attachment (ATT.8)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'attachmentversiondateview',
          type: 'datetime',
          props: {
            label: 'Version Date',
            keys: ['versiondateformat', 'attachmentversiondate'],
            required: true,

            attributes: {
              title:
                'Enter the date this version of the attachment was created (ATT.9)',
            },
          },
          validators: {
            customValidator: {
              expression: (control: any) => {
                let value = new Date();
                let today = new Date();
                if (control.value) {
                  if (
                    control.parent.controls.versiondateformat.value == '102'
                  ) {
                    const [dd, mm, yyyy] = control.value.split('/').map(Number);
                    value = new Date(yyyy, mm - 1, dd);
                    today = new Date();
                  } else {
                    const [mm, yyyy] = control.value.split('/').map(Number);
                    value = new Date(yyyy, mm - 1);
                    today = new Date();
                  }
                }

                return control.value == undefined || value <= today;
              },
              message: () => 'Date cannot be higher than today',
            },
          },
        },
        {
          key: 'attachmentversiondate',
          expressionProperties: {
            'model.attachmentversiondateview': (model) =>
              model && model.attachmentversiondateview === undefined
                ? DateUtil.importDate(
                  model.attachmentversiondate,
                  model.versiondateformat
                )
                : model == undefined ||
                  model.attachmentversiondateview === undefined
                  ? undefined
                  : model.attachmentversiondateview,
          },
        },
        {
          key: 'versiondateformat',
        },
      ],
    },
  ];

  sourceFields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'ev_Code',
          defaultValue: '',
          type: 'input',
          props: {
            label: 'EV Code',
            required: true,
            readonly: true,
            disabled: true,
            attributes: {
              title: 'EV Code',
            },
          },
          hideExpression: (model, formState, field) => {
            return field!.parent!.model.operationtype === 1;
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'operationtype',
          defaultValue: 1,
          type: 'select',
          props: {
            label: 'Operation Type',
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('operationtype'),
            valueProp: 'code',
            labelProp: 'name',
            required: true,
            readonly: true,
            disabled: true,
            attributes: {
              title: 'Operation Type',
            },
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      key: 'localnumber',
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'sourcename',
          type: 'input',
          props: {
            label: 'Source Name',
            required: true,

            attributes: {
              title: 'Source name (S.3)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'comments',
          type: 'textarea',
          props: {
            label: 'Comment',
            rows: 5,
            required: false,

            attributes: {
              title: 'Comment (S.4)',
            },
            maxLength: 500
          },
          expressions: {
            'props.required': (field: FormlyFieldConfig) => {
              return this.isNullifyOperation(this.getFieldOperationType(field));
            },
          },
        },
      ],
    },
  ];

  organisationFields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'ev_Code',
          defaultValue: '',
          type: 'input',
          props: {
            label: 'EV Code',
            required: true,
            readonly: true,
            disabled: true,
            attributes: {
              title: 'EV Code',
            },
          },
          hideExpression: (model, formState, field) => {
            const hide: boolean = field!.parent!.model.operationtype === 1;
            if (hide) {
              field!.formControl?.setValue(null);
            }
            return hide;
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'operationtype',
          defaultValue: 1,
          type: 'select',
          props: {
            label: 'Operation Type',
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('operationtype'),
            valueProp: 'code',
            labelProp: 'name',
            required: true,
            readonly: true,
            disabled: true,
            attributes: {
              title: 'Operation Type',
            },
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      key: 'localnumber',
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'type_Org',
          type: 'selectType',
          props: {
            label: 'Type',
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('typeorg'),
            valueProp: 'code',
            labelProp: 'name',
            required: true,
            readonly: true,
            disabled: true,
            attributes: {
              title: 'Type (O.1)',
            },
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'name_Org',
          type: 'input',
          props: {
            label: 'Organisation Name',
            required: true,

            attributes: {
              title: 'Organisation Name (O.2)',
            },
          },
          expressions: {
            'props.label': (field: FormlyFieldConfig) => {
              if (field.parent?.model.type_Org == 1) {
                return 'MAH Name';
              } else {
                return 'Sponsor Name';
              }
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'sme_Status',
          fieldGroup: [
            {
              key: 'value',
              type: 'selectType',
              props: {
                label: 'SME status',
                options: this.injector
                  .get(LookupGetService)
                  .loadCachedOptions('smestatus'),
                required: false,

                valueProp: 'code',
                labelProp: 'name',
                attributes: {
                  title: 'SME Status (O.19)',
                },
              },
              asyncValidators: {
                inOptions: this.inOptions,
              },
            },
            {
              key: 'name',
            },
            {
              key: 'resolutionmode',
            },
          ],
        },
      ],
      hideExpression: (field) => {
        return field?.type_Org == 2 || false;
      },
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'sme_Number',
          type: 'input',
          props: {
            label: 'SME number',
            attributes: {
              title: 'SME Number (O.20)',
            },
          },
          hideExpression: (field) => {
            return field?.type_Org == 2 || false;
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'organisationsenderid',
          type: 'input',
          wrappers: ['modal-wrapper'],
          props: {
            label: 'Organisation Sender ID',
            placeholder: '',
            required: false,

            lookup: {
              name: 'senderid',
              description: 'Select MAH Sender ID',
              columns: [
                new ListColumn('Num', ListDataColumn.TEXT, 'num'),
                new ListColumn('EV ID', ListDataColumn.TEXT, 'code'),
                new ListColumn('Name', ListDataColumn.TEXT, 'name'),
                new ListColumn('Country', ListDataColumn.TEXT, 'countryname'),
                new ListColumn('Connection', ListDataColumn.TEXT, 'trading'),
                new ListColumn(
                  'Type',
                  ListDataColumn.TEXT,
                  'organisationevtype'
                ),
                new ListColumn(
                  'EV Profile Type',
                  ListDataColumn.TEXT,
                  'evprofile'
                ),
              ],
              type: 'R',
            },
            readonly: true,
            attributes: {
              title: 'Organisation Sender ID (O.5)',
            },
          },
          validators: {
            lookupValidator: this.lookupSenderIdValidator,
          },
          expressions: {
            'props.label': (field: FormlyFieldConfig) => {
              if (field.parent?.model.type_Org == 1) {
                return 'MAH Sender ID';
              } else {
                return 'Sponsor Sender ID';
              }
            },
            'props.lookup.description': (field: FormlyFieldConfig) => {
              if (field.parent?.model.type_Org == 1) {
                return 'Select MAH Sender ID';
              } else {
                return 'Select Sponsor Sender ID';
              }
            },
          },
        },
        {
          key: 'resolutionmode',
        },
        {
          key: 'name',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'address',
          type: 'input',
          props: {
            label: 'Address',
            required: true,

            attributes: {
              title: 'Address (O.6)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'city',
          type: 'input',
          props: {
            label: 'City',
            required: true,

            attributes: {
              title: 'City (O.7)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'state',
          type: 'input',
          props: {
            label: 'State',
            required: false,

            attributes: {
              title: 'State (O.8)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'postcode',
          type: 'input',
          props: {
            label: 'Postcode',
            required: true,

            attributes: {
              title: 'Postcode (O.9)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          key: 'countrycode',
          className: 'flex-1',
          fieldGroup: [
            {
              key: 'value',
              type: 'selectType',
              props: {
                label: 'Country',
                required: true,
                options: this.injector
                  .get(LookupGetService)
                  .loadCachedOptions('country'),
                valueProp: 'code',
                labelProp: 'name',
                attributes: {
                  title: 'Country (O.10)',
                },
              },
              asyncValidators: {
                inOptions: this.inOptions,
              },
            },
            {
              key: 'name',
            },
            {
              key: 'resolutionmode',
            },
          ],
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'tel_Number',
          type: 'input',
          props: {
            label: 'Telephone Number',
            required: false,
            attributes: {
              title: 'Tel Number (O.11)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'tel_Extension',
          type: 'input',
          props: {
            label: 'Telephone Extension',
            required: false,

            attributes: {
              title: 'Tel Extension (O.12)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'tel_Countrycode',
          type: 'input',
          props: {
            label: 'Telephone Country Code',
            required: false,

            attributes: {
              title: 'Tel Country Code (O.13)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'fax_Number',
          type: 'input',
          props: {
            label: 'Fax Number',
            required: false,
            attributes: {
              title: 'Fax Number (O.14)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'fax_Extension',
          type: 'input',
          props: {
            label: 'Fax Extension',
            required: false,

            attributes: {
              title: 'Fax Extension (O.15)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'fax_Countrycode',
          type: 'input',
          props: {
            label: 'Fax Country Code',
            required: false,

            attributes: {
              title: 'Fax Country Code (O.16)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'email',
          type: 'input',
          props: {
            label: 'E-mail Address',
            required: false,
            autocomplete: 'PLEASE_DO_NOT_AUTOCOMPLETE',
            attributes: {
              title: 'E-mail Address (O.17)',
            },
          },
          validators: {
            customValidator: {
              expression: (c: any) =>
                c.value === undefined ||
                c.value === null ||
                c.value === '' ||
                /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}\s*$/.test(
                  c.value
                ),
              message: (_error: any, field: FormlyFieldConfig) =>
                `${field.formControl?.value ?? "''"
                } is not a valid email address`,
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'comments',
          type: 'textarea',
          props: {
            label: 'Comment',
            rows: 5,
            attributes: {
              title: 'Comment (O.18)',
            },
            maxLength: 500
          },
          expressions: {
            'props.required': (field: FormlyFieldConfig) => {
              return this.isNullifyOperation(this.getFieldOperationType(field));
            },
          },
        },
      ],
    },
  ];

  atcFields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'operationtype',
          type: 'select',
          defaultValue: 1,
          props: {
            label: 'Operation Type',
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('operationtype'),
            valueProp: 'code',
            labelProp: 'name',
            required: true,
            readonly: true,
            disabled: true,
            attributes: {
              title: 'Operation Type',
            },
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'type_Term',
          type: 'selectType',
          defaultValue: 3,
          props: {
            label: 'Type',
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('type_Term'),
            valueProp: 'code',
            labelProp: 'name',
            required: true,
            readonly: true,
            disabled: true,
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          key: 'newownerid',
          className: 'flex-1',
          type: 'input',
          wrappers: ['modal-wrapper'],
          hideExpression: () => {
            return !this.injector.get(AuthService).isEMAOrganisation();
          },
          props: {
            label: 'Owner ID',
            placeholder: '',
            attributes: {
              title:
                'Select the Owner for the Development ATC Code (Insert operation type only) (ST.ATC.3)',
            },
            lookup: {
              name: 'senderid',
              description: 'Select Owner',
              type: 'R',
              remoteMode: 'cache',
              columns: [
                new ListColumn('Num', ListDataColumn.TEXT, 'num'),
                new ListColumn('EV ID', ListDataColumn.TEXT, 'code'),
                new ListColumn('Name', ListDataColumn.TEXT, 'name'),
                new ListColumn('Country', ListDataColumn.TEXT, 'countryname'),
                new ListColumn('Connection', ListDataColumn.TEXT, 'trading'),
                new ListColumn(
                  'Type',
                  ListDataColumn.TEXT,
                  'organisationevtype'
                ),
                new ListColumn(
                  'EV Profile Type',
                  ListDataColumn.TEXT,
                  'evprofile'
                ),
              ],
            },
            readonly: true,
          },
          validators: {
            lookupValidator: this.lookupSenderIdValidator,
          },
          expressions: {
            'props.required': (field: FormlyFieldConfig) => {
              return (
                this.injector.get(AuthService).isEMAOrganisation() &&
                field.formControl?.parent?.get('type_Term')?.getRawValue() == 1
              );
            },
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'atccode',
          type: 'input',
          props: {
            label: 'ATC Code',
            required: true,
            attributes: {
              title: 'ATC Code (ST.ATC.2)',
            },
          },
          expressions: {
            'props.readonly': (field: FormlyFieldConfig) => {
              return !this.isInsertOperation(this.getFieldOperationType(field));
            },
            'props.disabled': (field: FormlyFieldConfig) => {
              return !this.isInsertOperation(this.getFieldOperationType(field));
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'atccode_Desc',
          type: 'input',
          props: {
            label: 'ATC Code Description',
            required: true,

            attributes: {
              title: 'ATC Code Description (ST.ATC.4)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'versiondateview',
          type: 'datetime',
          props: {
            label: 'Version Date',
            keys: ['versiondateformat', 'versiondate'],
            required: false,

            attributes: {
              title: 'Version Date (ST.ATC.6)',
            },
          },
          validators: {
            customValidator: {
              expression: (control: any) => {
                let value = new Date();
                let today = new Date();
                if (control.value) {
                  if (
                    control.parent.controls.versiondateformat.value ==
                    '102'
                  ) {
                    const [dd, mm, yyyy] = control.value
                      .split('/')
                      .map(Number);
                    value = new Date(yyyy, mm - 1, dd);
                    today = new Date();
                  } else {
                    const [mm, yyyy] = control.value.split('/').map(Number);
                    value = new Date(yyyy, mm - 1);
                    today = new Date();
                  }
                }
                return control.value == undefined || value <= today;
              },
              message: () => 'Date cannot be higher than today',
            },
          },
        },
        {
          key: 'versiondateformat',
        },
        {
          key: 'versiondate',

          expressionProperties: {
            'model.versiondateview': (model) =>
              model && model.versiondateview === undefined
                ? DateUtil.importDate(
                  model.versiondate,
                  model.versiondateformat
                )
                : model == undefined || model.versiondateview === undefined
                  ? undefined
                  : model.versiondateview,
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'comments',
          type: 'textarea',
          props: {
            label: 'Comment',
            rows: 5,
            required: false,

            attributes: {
              title: 'Comment (ST.ATC.7)',
            },
            maxLength: 500
          },
          expressions: {
            'props.required': (field: FormlyFieldConfig) => {
              return this.isNullifyOperation(this.getFieldOperationType(field));
            },
          },
        },
      ],
    },
  ];

  pharmaceuticalFormFields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'ev_Code',
          defaultValue: '',
          type: 'input',
          props: {
            label: 'EV Code',
            required: true,
            readonly: true,
            disabled: true,
            attributes: {
              title: 'EV Code',
            },
          },
          hideExpression: (model, formState, field) => {
            return field!.parent!.model.operationtype === 1;
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'operationtype',
          type: 'select',
          props: {
            label: 'Operation Type',
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('operationtype'),
            valueProp: 'code',
            labelProp: 'name',
            required: true,
            readonly: true,
            disabled: true,
            attributes: {
              title: 'Operation Type',
            },
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      key: 'localnumber',
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'type_Term',
          defaultValue: 3,
          type: 'selectType',
          props: {
            label: 'Type',
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('type_Term'),
            valueProp: 'code',
            labelProp: 'name',
            required: true,
            readonly: true,
            disabled: true,
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          key: 'newownerid',
          className: 'flex-1',
          type: 'input',
          wrappers: ['modal-wrapper'],
          hideExpression: () => {
            return !this.injector.get(AuthService).isEMAOrganisation();
          },
          props: {
            label: 'Owner ID',
            placeholder: '',
            attributes: {
              title:
                'Select the Owner for the Development ATC Code (Insert operation type only) (ST.PF.4)',
            },
            lookup: {
              name: 'senderid',
              description: 'Select Owner',
              type: 'R',
              remoteMode: 'cache',
              columns: [
                new ListColumn('Num', ListDataColumn.TEXT, 'num'),
                new ListColumn('EV ID', ListDataColumn.TEXT, 'code'),
                new ListColumn('Name', ListDataColumn.TEXT, 'name'),
                new ListColumn('Country', ListDataColumn.TEXT, 'countryname'),
                new ListColumn('Connection', ListDataColumn.TEXT, 'trading'),
                new ListColumn(
                  'Type',
                  ListDataColumn.TEXT,
                  'organisationevtype'
                ),
                new ListColumn(
                  'EV Profile Type',
                  ListDataColumn.TEXT,
                  'evprofile'
                ),
              ],
            },
            readonly: true,
          },
          validators: {
            lookupValidator: this.lookupSenderIdValidator,
          },
          expressions: {
            'props.required': (field: FormlyFieldConfig) => {
              field.formControl?.parent?.get('type_Term')?.getRawValue();
              return (
                this.injector.get(AuthService).isEMAOrganisation() &&
                field.formControl?.parent?.get('type_Term')?.getRawValue() == 1
              );
            },
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'name_Pharmform',
          type: 'input',
          props: {
            label: 'Pharmaceutical Form Name',
            required: true,

            attributes: {
              title: 'Pharmaceutical Form Name (ST.PF.5)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'previous_Ev_Code',
          type: 'input',
          props: {
            label: 'Previous EV Code',
            required: false,

            attributes: {
              title: 'Previous EV Code (ST.PF.8)',
            },
          },
          validators: {
            customValidator: {
              expression: (control: any, field: FormlyFieldConfig) => {
                const phfType = control.root.controls.type_Term.value;
                return !(
                  control.value &&
                  (control.value != '' || control.value != null) &&
                  phfType == 1
                );
              },
              message: () =>
                'Cannot be Filled for Development Pharmaceutical Forms',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'versiondateview',
          type: 'datetime',
          props: {
            label: 'Version Date',
            keys: ['versiondateformat', 'versiondate'],
            required: false,

            attributes: {
              title: 'Version Date (ST.PF.7)',
            },
          },
          validators: {
            customValidator: {
              expression: (control: any) => {
                let value = new Date();
                let today = new Date();
                if (control.value) {
                  if (
                    control.parent.controls.versiondateformat.value ==
                    '102'
                  ) {
                    const [dd, mm, yyyy] = control.value
                      .split('/')
                      .map(Number);
                    value = new Date(yyyy, mm - 1, dd);
                    today = new Date();
                  } else {
                    const [mm, yyyy] = control.value.split('/').map(Number);
                    value = new Date(yyyy, mm - 1);
                    today = new Date();
                  }
                }
                return control.value == undefined || value <= today;
              },
              message: () => 'Date cannot be higher than today',
            },
          },
        },
        {
          key: 'versiondateformat',
        },
        {
          key: 'versiondate',
          expressionProperties: {
            'model.versiondateview': (model) =>
              model && model.versiondateview === undefined
                ? DateUtil.importDate(
                  model.versiondate,
                  model.versiondateformat
                )
                : model == undefined || model.versiondateview === undefined
                  ? undefined
                  : model.versiondateview,
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'comments',
          type: 'textarea',
          props: {
            label: 'Comment',
            rows: 5,
            required: false,

            attributes: {
              title: 'Comment (ST.PF.9)',
            },
            maxLength: 500
          },
          expressions: {
            'props.required': (field: FormlyFieldConfig) => {
              return this.isNullifyOperation(this.getFieldOperationType(field));
            },
          },
        },
      ],
    },
  ];

  administrationRouteFields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'ev_Code',
          defaultValue: '',
          type: 'input',
          props: {
            label: 'EV Code',
            required: true,
            readonly: true,
            disabled: true,
            attributes: {
              title: 'EV Code',
            },
          },
          hideExpression: (model, formState, field) => {
            return field!.parent!.model.operationtype === 1;
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'operationtype',
          defaultValue: 1,
          type: 'select',
          props: {
            label: 'Operation Type',
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('operationtype'),
            valueProp: 'code',
            labelProp: 'name',
            required: true,
            readonly: true,
            disabled: true,
            attributes: {
              title: 'Operation Type',
            },
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      key: 'localnumber',
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'type_Term',
          defaultValue: 3,
          type: 'selectType',
          props: {
            label: 'Type',
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('type_Term'),
            valueProp: 'code',
            labelProp: 'name',
            required: true,
            readonly: true,
            disabled: true,
            attributes: {
              title: 'Type (ST.AR.1)',
            },
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          key: 'newownerid',
          className: 'flex-1',
          type: 'input',
          wrappers: ['modal-wrapper'],
          hideExpression: () => {
            return !this.injector.get(AuthService).isEMAOrganisation();
          },
          props: {
            label: 'Owner ID',
            placeholder: '',
            lookup: {
              name: 'senderid',
              description: 'Select Owner',
              type: 'R',
              remoteMode: 'cache',
              columns: [
                new ListColumn('Num', ListDataColumn.TEXT, 'num'),
                new ListColumn('EV ID', ListDataColumn.TEXT, 'code'),
                new ListColumn('Name', ListDataColumn.TEXT, 'name'),
                new ListColumn('Country', ListDataColumn.TEXT, 'countryname'),
                new ListColumn('Connection', ListDataColumn.TEXT, 'trading'),
                new ListColumn(
                  'Type',
                  ListDataColumn.TEXT,
                  'organisationevtype'
                ),
                new ListColumn(
                  'EV Profile Type',
                  ListDataColumn.TEXT,
                  'evprofile'
                ),
              ],
            },
            readonly: true,
            attributes: {
              title:
                'Select the Owner of the Development ATC Code (Insert operation type only) (ST.AR.4)',
            },
          },
          validators: {
            lookupValidator: this.lookupSenderIdValidator,
          },
          expressions: {
            'props.required': (field: FormlyFieldConfig) => {
              return (
                this.injector.get(AuthService).isEMAOrganisation() &&
                field.formControl?.parent?.get('type_Term')?.getRawValue() == 1
              );
            },
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'name_Admroute',
          type: 'input',
          props: {
            label: 'Administration Route Name',
            required: true,

            attributes: {
              title: 'Administration Route Name (ST.AR.5)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'versiondateview',
          type: 'datetime',
          props: {
            label: 'Version Date',
            keys: ['versiondateformat', 'versiondate'],
            required: false,

            attributes: {
              title: 'Version Date (ST.AR.7)',
            },
          },
          validators: {
            customValidator: {
              expression: (control: any) => {
                let value = new Date();
                let today = new Date();
                if (control.value) {
                  if (
                    control.parent.controls.versiondateformat.value ==
                    '102'
                  ) {
                    const [dd, mm, yyyy] = control.value
                      .split('/')
                      .map(Number);
                    value = new Date(yyyy, mm - 1, dd);
                    today = new Date();
                  } else {
                    const [mm, yyyy] = control.value.split('/').map(Number);
                    value = new Date(yyyy, mm - 1);
                    today = new Date();
                  }
                }
                return control.value == undefined || value <= today;
              },
              message: () => 'Date cannot be higher than today',
            },
          },
        },
        {
          key: 'versiondateformat',
        },
        {
          key: 'versiondate',
          expressionProperties: {
            'model.versiondateview': (model) =>
              model && model.versiondateview === undefined
                ? DateUtil.importDate(
                  model.versiondate,
                  model.versiondateformat
                )
                : model == undefined ||
                  model == undefined ||
                  model.versiondateview === undefined
                  ? undefined
                  : model.versiondateview,
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'previous_Ev_Code',
          type: 'input',
          props: {
            label: 'Previous EV Code',
            required: false,

            attributes: {
              title: 'Previous EV Code (ST.AR.8)',
            },
          },
          validators: {
            customValidator: {
              expression: (control: any, field: FormlyFieldConfig) => {
                const admType = control.root.controls.type_Term.value;
                return !(
                  control.value &&
                  (control.value != '' || control.value != null) &&
                  admType == 1
                );
              },
              message: () =>
                'Cannot be Filled for Development Administration Routes',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'comments',
          type: 'textarea',
          props: {
            label: 'Comment',
            rows: 5,
            required: false,

            attributes: {
              title: 'Comment (ST.AR.9)',
            },
            maxLength: 500
          },
          expressions: {
            'props.required': (field: FormlyFieldConfig) => {
              return this.isNullifyOperation(this.getFieldOperationType(field));
            },
          },
        },
      ],
    },
  ];

  masterFileLocationFields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'ev_Code',
          defaultValue: '',
          type: 'input',
          props: {
            label: 'EV Code',
            required: true,
            readonly: true,
            disabled: true,
            attributes: {
              title: 'EV Code',
            },
          },
          hideExpression: (model, formState, field) => {
            return field!.parent!.model.operationtype === 1;
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'operationtype',
          defaultValue: 1,
          type: 'select',
          props: {
            label: 'Operation Type',
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('operationtype'),
            valueProp: 'code',
            labelProp: 'name',
            required: true,
            readonly: true,
            disabled: true,
            attributes: {
              title: 'Operation Type',
            },
          },
          asyncValidators: {
            inOptions: this.inOptions,
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      key: 'localnumber',
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'mflcompany',
          type: 'input',
          props: {
            label: 'Company',
            required: false,

            attributes: {
              title: 'Company (MF.3)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'mfldepartment',
          type: 'input',
          props: {
            label: 'Department',
            required: false,

            attributes: {
              title: 'Department (MF.4)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'mflbuilding',
          type: 'input',
          props: {
            label: 'Building',
            required: false,

            attributes: {
              title: 'Building (MF.5)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'mflstreet',
          type: 'input',
          props: {
            label: 'Street',
            required: true,

            attributes: {
              title: 'Street (MF.6)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'mflcity',
          type: 'input',
          props: {
            label: 'City',
            required: true,

            attributes: {
              title: 'City (MF.7)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'mflstate',
          type: 'input',
          props: {
            label: 'State',
            required: false,

            attributes: {
              title: 'State (MF.8)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'mflpostcode',
          type: 'input',
          props: {
            label: 'Postcode',
            required: true,

            attributes: {
              title: 'Postcode (MF.9)',
            },
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'mflcountrycode',
          fieldGroup: [
            {
              key: 'value',
              type: 'selectType',
              props: {
                label: 'Country',
                required: true,
                options: this.injector
                  .get(LookupGetService)
                  .loadCachedOptions('country'),
                valueProp: 'code',
                labelProp: 'name',
                attributes: {
                  title: 'Country (MF.10)',
                },
              },
              asyncValidators: {
                inOptions: this.inOptions,
              },
            },
            {
              key: 'name',
            },
            {
              key: 'resolutionmode',
            },
          ],
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'comments',
          type: 'textarea',
          props: {
            label: 'Comment',
            required: false,

            rows: 5,
            attributes: {
              title: 'Comment (MF.11)',
            },
            maxLength: 500
          },
          expressions: {
            'props.required': (field: FormlyFieldConfig) => {
              return this.isNullifyOperation(this.getFieldOperationType(field));
            },
          },
        },
      ],
    },
  ];

  olddrugIngredientItemFields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'substancecode',
          fieldGroup: [
            {
              type: 'input',
              key: 'value',
              wrappers: ['modal-wrapper'],
              props: {
                label: 'Substance',
                lookup: {
                  name: 'substance',
                  description: 'Select Substance',
                  type: 'LR',
                  parameters: null,
                  columns: [
                    new ListColumn('Num', ListDataColumn.TEXT, 'num'),
                    new ListColumn('EV Code', ListDataColumn.TEXT, 'code'),
                    new ListColumn(
                      'Substance Name',
                      ListDataColumn.TEXT,
                      'name'
                    ),
                    new ListColumn('Type', ListDataColumn.TEXT, 'type'),
                    new ListColumn('Language', ListDataColumn.TEXT, 'language'),
                    new ListColumn(
                      'Validated',
                      ListDataColumn.DATE,
                      'validated'
                    ),
                  ],
                },
                disabled: true,
                readonly: true,
                attributes: {
                  title:
                    'The substance name of the ingredient(s) indicated as active/excipient/adjuvant in line with the description of the ingredients referenced in the SmPC must be specified as per guidance described in Chapter 3.II. (PP.ACT.1/PP.EXC.1/PP.ADJ.1)',
                },
              },
              expressions: {
                'props.lookup.parameters': (field: FormlyFieldConfig) => {
                  if (
                    this.injector.get(Art57Section).getSelectedSection()
                      .selectedNode.parent?.parent?.parent?.parent?.data.model
                      .type == 2
                  ) {
                    //Authorised
                    return [['type', 2]];
                  } else {
                    //Development
                    //No filters needed
                    return null;
                  }
                },
              },
            },
            {
              key: 'name',
            },
            {
              key: 'resolutionmode',
              defaultValue: 2,
            },
          ],
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'roleofingredient',
          type: 'radio',
          props: {
            label: 'Role of Ingredient',
            options: this.injector
              .get(LookupGetService)
              .loadCachedOptions('roleofingredient'),
            valueProp: 'code',
            labelProp: 'name',
            disabled: true,
            readonly: true,
            attributes: {
              title: 'Role of Ingredient (PP.ACT/PP.EXC/PP.ADJ)',
            },
          },
        },
        {
          key: 'name',
        },
        {
          key: 'resolutionmode',
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'concentrationtype',
          defaultValue: '',
          type: 'input',
          props: {
            label: 'Type of Concentration',
            readonly: true,
            disabled: true,
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'concentrationunitcode',
          defaultValue: '',
          type: 'input',
          props: {
            label: 'Concentration Unit Code',
            readonly: true,
            disabled: true,
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'concentration',
          defaultValue: '',
          type: 'input',
          props: {
            label: 'Concentration',
            readonly: true,
            disabled: true,
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          className: 'flex-1',
          key: 'concentration2',
          defaultValue: '',
          type: 'input',
          props: {
            label: 'Concentration 2 (range)',
            readonly: true,
            disabled: true,
          },
        },
      ],
    },
  ];
}
